/**
 * Task #3406 — Incentivazione (commissioning): tipi di dominio condivisi.
 *
 * SoT analisi: docs/incentivazione-analisi.md (§1–§4). Questi tipi sono la
 * controparte TypeScript dello schema `commissioning_*` (migrations
 * 20261006000000..20261006030000) e vengono consumati da backend E frontend
 * (zero `any`). Gli schemi zod degli input vivono in
 * `packages/shared/src/schemas/commissioning.ts`.
 */
// Task #3454 — estensione tipizzata dello snapshot ledger (runtime v3).
import type { CommissioningLedgerSnapshotV3Extension } from './commissioning-runtime-v3';
// Task #3475 — metodo di rateizzazione esterna (rid/cdc) snapshottato nell'evento sell-out.
import type { InstallmentRepayment, PriceListType } from '../pos/pricelist';
import type { PaymentModeCreditHolder } from '../pos/payment-modes';
import type { CommissioningCommonEconomy } from '../schemas/commissioning-competition-context';
import type {
  CommissioningThresholdCategory,
  CommissioningThresholdEconomicsTrace,
  CommissioningThresholdSurcharge,
  CommissioningThresholdValidity,
} from '../schemas/commissioning-threshold-economics';
import type { ProductNature } from '../pos/admin-inputs';
import type { z } from 'zod';
import type { commissioningCrossRefMeasureSchema } from '../schemas/commissioning';
import type { CommissioningInstanceThresholdMetadata } from '../schemas/commissioning-declarative-thresholds';

// ---------------------------------------------------------------------------
// Union di dominio (allineate 1:1 ai CHECK a DB)
// ---------------------------------------------------------------------------

/** Famiglie di gara (CHECK su commissioning_competitions.family). */
export const COMMISSIONING_COMPETITION_FAMILIES = ['insegna', 'sellin', 'risorse'] as const;
export type CommissioningCompetitionFamily = typeof COMMISSIONING_COMPETITION_FAMILIES[number];

/** Stati ciclo vita gara (CHECK su commissioning_competitions.status). */
export const COMMISSIONING_COMPETITION_STATUSES = [
  'draft',
  'active',
  'suspended',
  'closed',
  'archived',
] as const;
export type CommissioningCompetitionStatus = typeof COMMISSIONING_COMPETITION_STATUSES[number];

/** Tipo entità ESCLUSIVO dei cluster (CHECK su commissioning_clusters.entity_type). */
export const COMMISSIONING_CLUSTER_ENTITY_TYPES = ['rs', 'pdv', 'risorsa'] as const;
export type CommissioningClusterEntityType = typeof COMMISSIONING_CLUSTER_ENTITY_TYPES[number];

/**
 * Appartenenza: famiglia gara ↔ tipo entità dei cluster. Una gara insegna
 * raggruppa negozi (pdv), una gara sell-in ragioni sociali (rs), una gara
 * risorse persone (risorsa). Unica fonte per libreria cluster, fotografie gara
 * e matrice: il tipo entità della gara è DERIVATO dalla famiglia, mai scelto.
 */
export const COMMISSIONING_FAMILY_ENTITY_TYPE: Readonly<
  Record<CommissioningCompetitionFamily, CommissioningClusterEntityType>
> = { insegna: 'pdv', sellin: 'rs', risorse: 'risorsa' };

export const COMMISSIONING_ENTITY_TYPE_FAMILY: Readonly<
  Record<CommissioningClusterEntityType, CommissioningCompetitionFamily>
> = { pdv: 'insegna', rs: 'sellin', risorsa: 'risorse' };

/**
 * Tipi configuratore ATTIVI (commissioning_configurator_types.code con
 * is_active=true). `kpi_coorte` è stato unificato in `bonus_malus`
 * («Bonus/Malus su KPI»): la riga di catalogo resta disattivata per il CHECK
 * della colonna, ma nessun template, wizard o dispatcher la usa più.
 */
export const COMMISSIONING_CONFIGURATOR_TYPE_CODES = [
  'soglie',
  'gettone',
  'bonus_malus',
  'classifica',
  'aggregazione',
] as const;
export type CommissioningConfiguratorTypeCode =
  typeof COMMISSIONING_CONFIGURATOR_TYPE_CODES[number];

/** Codici ritirati: presenti nel CHECK del catalogo (is_active=false), mai proponibili. */
export const COMMISSIONING_RETIRED_CONFIGURATOR_TYPE_CODES = ['kpi_coorte'] as const;
export type CommissioningRetiredConfiguratorTypeCode =
  typeof COMMISSIONING_RETIRED_CONFIGURATOR_TYPE_CODES[number];

/**
 * Tipi la cui economia è separata in STRUTTURA (template) e VALORI per
 * configuratore × cluster (editor «Valori per cluster», stato valori,
 * preflight/attivazione/pubblicazione fail-closed): Soglie (bande),
 * Bonus/Malus su KPI (regole), Classifica (fasce di posizione) e
 * Aggregazione (sorgenti e livelli). Un solo elenco per BE e FE.
 */
export const COMMISSIONING_VALUES_STRUCTURE_TYPE_CODES = ['soglie', 'bonus_malus', 'classifica', 'aggregazione'] as const satisfies readonly CommissioningConfiguratorTypeCode[];
export type CommissioningValuesStructureTypeCode = typeof COMMISSIONING_VALUES_STRUCTURE_TYPE_CODES[number];
export function commissioningTypeHasValuesStructure(typeCode: string | null | undefined): typeCode is CommissioningValuesStructureTypeCode {
  return COMMISSIONING_VALUES_STRUCTURE_TYPE_CODES.some((code) => code === typeCode);
}
/** Etichette leggibili della struttura a valori per tipo (messaggi BE/FE). */
export const COMMISSIONING_VALUES_STRUCTURE_LABELS: Record<CommissioningValuesStructureTypeCode, string> = {
  soglie: 'Soglie',
  bonus_malus: 'Bonus/Malus su KPI',
  classifica: 'Classifica',
  aggregazione: 'Aggregazione',
};

/** Finestra di osservazione di una regola Bonus/Malus su KPI (assente = periodo di gara). */
export const COMMISSIONING_BONUS_MALUS_WINDOWS = ['period', 'rolling_30d', 'rolling_90d'] as const;
export type CommissioningBonusMalusWindow = typeof COMMISSIONING_BONUS_MALUS_WINDOWS[number];

/** Effetti dichiarabili da una regola Bonus/Malus su KPI: malus sotto la banda morta, bonus sopra. */
export const COMMISSIONING_BONUS_MALUS_EFFECTS = ['malus', 'bonus'] as const;
export type CommissioningBonusMalusEffect = typeof COMMISSIONING_BONUS_MALUS_EFFECTS[number];

/** Tipi arco del DAG interazioni (CHECK su commissioning_configurator_interactions.edge_type). */
export const COMMISSIONING_INTERACTION_EDGE_TYPES = [
  'valenza',
  'gettone',
  'paletto_sblocco',
  'blocco_esecuzione',
  /** Task #3494 — scala il payout del figlio di un fattore. */
  'moltiplicatore',
] as const;
export type CommissioningInteractionEdgeType = typeof COMMISSIONING_INTERACTION_EDGE_TYPES[number];

/**
 * Metriche del padre disponibili per la condizione `parent_outcome`.
 * - `band_reached`: indice di banda raggiunta (1-based, 0 = nessuna); solo
 *   per padri di tipo `soglie`; su altri tipi → non valutabile (null).
 * - `valenza`: valenza del padre al momento della cascata DAG.
 * - `fatturato`: amount_eur del padre al momento della cascata DAG.
 * - `count`: numero di righe del periodo (period_line_count).
 * - `tgt_pct`: percentuale raggiungimento TGT; null se il padre non ha TGT.
 */
export const COMMISSIONING_EDGE_CONDITION_METRICS = [
  'band_reached',
  'valenza',
  'fatturato',
  'count',
  'tgt_pct',
] as const;
/** Tipo CAP per sotto-categoria (CHECK su commissioning_configurator_instances.cap_type). */
export const COMMISSIONING_CAP_TYPES = ['absolute', 'percentage_of_total'] as const;
export type CommissioningCapType = typeof COMMISSIONING_CAP_TYPES[number];

/**
 * Task CAP evoluto — Metrica su cui insiste il tetto CAP (CHECK su
 * commissioning_configurator_instances.cap_metric):
 * - `amount_eur`: tetto sull'importo € maturato (comportamento storico,
 *   default quando la metrica è assente);
 * - `valenza`: tetto sulla valenza contabile cumulata — gli eventi oltre il
 *   tetto non saturano ulteriormente i cumulati (valenza riga clampata) e il
 *   payout è ridotto in proporzione;
 * - `count`: tetto sul numero di eventi remunerati nel periodo — gli eventi
 *   oltre l'N-esimo non pagano.
 * Recinto del tipo: `valenza`/`count` sono ammesse SOLO per il tipo `soglie`.
 */
export const COMMISSIONING_CAP_METRICS = ['amount_eur', 'valenza', 'count'] as const;
/** Scope di counting dell'istanza (DA DOVE si conta; cluster = CHI partecipa). */
export const COMMISSIONING_INSTANCE_SCOPES = ['pdv', 'rs', 'multi_rs', 'user', 'multi_user', 'multi_pdv'] as const;
export type CommissioningInstanceScope = typeof COMMISSIONING_INSTANCE_SCOPES[number];

/** Tipi dato delle variabili L1 (CHECK su commissioning_variables.data_type). */
export const COMMISSIONING_VARIABLE_DATA_TYPES = [
  'number',
  'currency',
  'percent',
  'boolean',
  'text',
  'date',
] as const;
export type CommissioningVariableDataType = typeof COMMISSIONING_VARIABLE_DATA_TYPES[number];

/**
 * Task #3409 — Codici delle variabili L1 seed di piattaforma (migration
 * 20261006000000: 21 base + 5 anagrafica RS/PDV + 4 computed aggregate).
 * SoT per riconoscerle in UI: badge "Piattaforma" e delete disabilitato.
 * Il seed usa ON CONFLICT DO NOTHING → i codici sono stabili per-tenant.
 */
export const COMMISSIONING_SEED_VARIABLE_CODES = [
  // Valori Base
  'valenza', 'gettone_contrattuale', 'gettone_gara', 'canone',
  // Economico
  'valore_prodotto', 'valore_vendita', 'margine', 'sconto_percentuale', 'entry_fee',
  // Volumi & Quantità
  'volumi', 'n_pezzi_composizione', 'durata_contratto_mesi',
  // Bonus & Incentivi
  'addon_presente', 'addon_count', 'promo_flag',
  // Qualità
  'tipo_attivazione', 'operatore_donating', 'is_mnp',
  // Anagrafica & Contesto
  'metodo_pagamento', 'metodo_rateizzazione', 'canale_vendita', 'insegna',
  // Task #4322 — struttura di credito della riga (snapshot vendita)
  // (`titolare_credito` rimossa dal catalogo: non è un criterio di regola, resta solo lo snapshot amministrativo)
  'modalita_pagamento_insegna', 'ente_finanziante', 'importo_finanziato', 'numero_rate',
  'canone_affitto_mensile', 'pdv_codice', 'pdv_citta', 'rs_ragione_sociale', 'rs_partita_iva',
  // Computed aggregate (period_aggregate.*)
  'tasso_mnp_out', 'n_vendite_periodo', 'fatturato_periodo', 'valenza_periodo',
] as const;
export type CommissioningSeedVariableCode = typeof COMMISSIONING_SEED_VARIABLE_CODES[number];

/** Azioni dell'access-log lettera gara. */
export const COMMISSIONING_ACK_ACTIONS = ['viewed', 'downloaded', 'acknowledged'] as const;
export type CommissioningAckAction = typeof COMMISSIONING_ACK_ACTIONS[number];

/** Modalità di match dei rami L2: primo match vince vs moltiplicatori additivi. */
/**
 * Famiglia evento esplicita del Value Package e dei
 * configuratori. Il motore rifiuta prima del calcolo package incompatibili
 * con la sorgente dell'evento (un evento CRM non riceve basi di un package
 * vendita). Coincide con i source_type degli eventi normalizzati.
 */
export const COMMISSIONING_VP_EVENT_FAMILIES = ['sell_out', 'sell_in', 'crm'] as const;
/** Placeholder variabile L1 nei predicati (es. '@valenza'). */
export type CommissioningVariableRef = `@${string}`;

/** Operatori dei predicati L3. */
export const COMMISSIONING_CONDITION_OPERATORS = [
  'gt',
  'lt',
  'gte',
  'lte',
  'eq',
  'neq',
  /** Intervallo inclusivo [min, max] — richiede `values: [min, max]` (numerici o date ISO). */
  'between',
  'pct_above',
  'pct_below',
  'contains',
  'starts_with',
  'ends_with',
  'is_empty',
  'is_not_empty',
  'in_list',
  'not_in_list',
  /** Date relative: la data è negli ultimi N giorni (incluso oggi) — `value` = N. */
  'in_last_days',
  /** Date relative: la data è nei prossimi N giorni (incluso oggi) — `value` = N. */
  'in_next_days',
  /** Date relative: la data cade nel mese corrente — operatore unario. */
  'in_current_month',
] as const;
export type CommissioningConditionOperator = typeof COMMISSIONING_CONDITION_OPERATORS[number];

/** Valore scalare confrontabile in un predicato. */
export type CommissioningConditionValue = string | number | boolean;

/**
 * @deprecated Usa `CommissioningKernelCompareLeaf` da `commissioning-kernel`.
 * Rimosso da `CommissioningFunction.condition_tree` (ora `CommissioningKernelNode`).
 * Mantenuto solo come alias per i consumer legacy già in produzione.
 */
export type CommissioningConditionLeaf =
  import('./commissioning-kernel').CommissioningKernelCompareLeaf;

/**
 * @deprecated Usa `CommissioningKernelNode` da `commissioning-kernel`.
 * `CommissioningFunction.condition_tree` è ora tipato `CommissioningKernelNode`.
 */
export type CommissioningConditionNode =
  import('./commissioning-kernel').CommissioningKernelNode;

// ---------------------------------------------------------------------------
// Task #3450 — Catalogo add-on commerciali deterministici (v2).
// Union di dominio allineate 1:1 ai CHECK a DB.
// ---------------------------------------------------------------------------

/** Tipi risposta dei parametri commerciali (CHECK su commissioning_addon_parameters.value_type). */
export const COMMISSIONING_ADDON_PARAM_TYPES = [
  'boolean',
  'single_select',
  'multi_select',
  'number',
  'text',
] as const;
export type CommissioningAddonParamType = typeof COMMISSIONING_ADDON_PARAM_TYPES[number];

/** Stati versione add-on (CHECK su commissioning_addon_versions.status). */
export const COMMISSIONING_ADDON_VERSION_STATUSES = ['draft', 'published'] as const;
export type CommissioningAddonVersionStatus = typeof COMMISSIONING_ADDON_VERSION_STATUSES[number];

/** Stati risposta: no/mancante/non-applicabile sono stati DIVERSI. */
export const COMMISSIONING_ADDON_ANSWER_STATES = ['answered', 'missing', 'not_applicable'] as const;
export type CommissioningAddonAnswerState = typeof COMMISSIONING_ADDON_ANSWER_STATES[number];

/** Provenienza della risposta (questa task: la cassa scrive 'operator'). */
export const COMMISSIONING_ADDON_ANSWER_SOURCES = ['operator', 'api', 'ocr'] as const;
export type CommissioningAddonAnswerSource = typeof COMMISSIONING_ADDON_ANSWER_SOURCES[number];

/** Livello di specificità dell'applicabilità a matrioska (0..3). */
export const COMMISSIONING_ADDON_SCOPE_LEVELS = [
  'driver',
  'category',
  'product_type',
  'product',
] as const;
export type CommissioningAddonScopeLevel = typeof COMMISSIONING_ADDON_SCOPE_LEVELS[number];

/**
 * Valore CANONICO di una risposta add-on, discriminato per tipo parametro:
 * i select trasportano gli ID valore di lista (identificatori, mai etichette);
 * code/label vengono fotografati server-side nello snapshot.
 */
export type CommissioningAddonAnswerValue =
  | { type: 'boolean'; value: boolean }
  | { type: 'number'; value: number }
  | { type: 'text'; value: string }
  | { type: 'single_select'; value_id: string }
  | { type: 'multi_select'; value_ids: string[] };

/** Valore runtime addon.* nel motore: scalare o array canonico (multiselect). */
export type CommissioningAddonRuntimeValue = string | number | boolean | null | string[];
/** Namespace addon.* del RuntimeContext (chiavi = parameter code canonici). */
export type CommissioningAddonRuntimeNamespace = Record<string, CommissioningAddonRuntimeValue>;

/** Passo del wizard React dedicato al tipo (configurator_types.wizard_steps). */
export interface CommissioningWizardStep {
  key: string;
  label: string;
  description?: string;
}

/**
 * Fonti economiche dichiarabili dal configuratore a soglie (dichiarazione a
 * priori, combinabili in AND). Il gettone CONTRATTUALE è escluso by-design dal
 * tipo soglie (recinto: né fonte né target leve istanza).
 */
export const COMMISSIONING_THRESHOLD_SOURCES = [
  /** Moltiplicatore del contributo canone (dal VP), componibile con canone_factor. */
  'canone_multiplier',
  /** Moltiplicatore del gettone gara per-evento (dal VP). */
  'gettone_gara_multiplier',
  /** € per unità di count (valore di banda × unità dell'evento). */
  'per_unit_eur',
  /** Premio flat € one-shot al primo raggiungimento della banda nel periodo. */
  'flat_eur',
  /** % sull'importo netto dell'evento (`lineTotal`): valore di banda / 100 × importo. */
  'percent_of_amount',
] as const;

/**
 * Scala dei limiti di banda: `absolute` = i valori Da/A valgono così come
 * dichiarati; `per_store` = sono "per punto vendita" e per un partecipante
 * Ragione Sociale vengono moltiplicati per il numero di PdV congelato nel
 * contesto comune (`store_count`); per PdV e risorse il fattore è 1.
 */
export const COMMISSIONING_THRESHOLD_SCALES = ['absolute', 'per_store'] as const;
export type CommissioningThresholdScale = typeof COMMISSIONING_THRESHOLD_SCALES[number];
/**
 * Banda del configuratore Soglie come la descrive il TEMPLATE: solo struttura
 * (identità stabile, nome, ordine = posizione nell'array, categorie e
 * maggiorazioni con condizione e parametri da valorizzare, requisito di
 * ammissione). Nessun valore numerico: Da/A, tolleranza e importi vivono nei
 * valori per configuratore × cluster (`CommissioningThresholdValues`).
 */
export interface CommissioningThresholdBandStructure {
  /** Identità stabile della banda: chiave dei valori per cluster/cella/membro. */
  id: string;
  /** Nome operativo della banda (visibile nei report). */
  name: string;
  /** Categorie alternative: prima condizione che MATCHA per priorità crescente. */
  categories?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdCategoryStructure[];
  /** Maggiorazioni cumulative (delta additivi sui parametri). */
  surcharges?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdSurchargeStructure[];
  /** A published L3 condition controls admission; AND/OR remain inside that function. */
  admission?: { functionId: string };
}

/**
 * Banda EFFETTIVA consumata dal motore: struttura del template composta con i
 * valori del livello più specifico (membro > cella driver > cluster > istanza).
 * Non è mai salvata: la produce `composeCommissioningThresholdSection`.
 */
export interface CommissioningThreshold {
  /** Stable identity, independent of ordering and display name. */
  id: string;
  /** Nome banda (visibile nei report). */
  name: string;
  from: number;
  to?: number;
  /** Fonte 'canone_multiplier': moltiplicatore del contributo canone. */
  canoneMultiplier?: number;
  /** Fonte 'gettone_gara_multiplier': moltiplicatore del gettone gara. */
  gettoneGaraMultiplier?: number;
  /** Fonte 'per_unit_eur': € per unità di count. */
  perUnitEur?: number;
  /** Fonte 'flat_eur': premio one-shot al primo raggiungimento della banda. */
  flatEur?: number;
  /** Fonte 'percent_of_amount': % (0–100) sull'importo netto dell'evento. */
  percentOfAmount?: number;
  /**
   * Tolleranza % di ingresso banda (0–99). La soglia effettiva di ingresso
   * diventa `from × (1 − tolerancePct/100)`: entro la tolleranza la banda è
   * pienamente raggiunta (fonti a valori pieni, incluso il flat one-shot).
   * Abbassa SOLO il punto di ingresso: i confini nominali `from`/`to` restano
   * la fonte di verità per ordinamento e validazione anti-sovrapposizione.
   */
  tolerancePct?: number;
  /** Alternative parameters, first MATCH by ascending unique priority. */
  categories?: CommissioningThresholdCategory[];
  /** Cumulative additive deltas, never factors or percentages of the total. */
  surcharges?: CommissioningThresholdSurcharge[];
  /** A published L3 condition controls admission; AND/OR remain inside that function. */
  admission?: { functionId: string };
}

/** Asse di aggregazione del perimetro gate (indipendente dall'asse dell'istanza). */
export type CommissioningGateAxis = 'rs' | 'pdv' | 'user';
/**
 * Regola gettone (tipo 'gettone'): SELETTORE di eventi. Le regole del
 * template sono valutate in ordine sull'evento; la prima condizione vera
 * seleziona l'evento, che riceve il gettone gara del Value Package
 * (`commissioning_value_package_bases.gettone_gara` + leve). Nessuna regola
 * vera → gettone gara 0; condizione non valutabile → 0 con motivo esplicito.
 * Lista assente/vuota → ogni evento ammesso riceve il gettone gara del VP.
 * Nessun importo vive nel template: se serve un importo diverso per
 * condizione lo strumento sono gli scenari del VP.
 */
export interface CommissioningGettoneRule {
  label?: string;
  /** Condizione L3 che seleziona l'evento (assente = regola sempre vera). */
  functionId?: string;
  /**
   * @deprecated Importo storico mai letto dal motore: il gettone pagato è
   * quello del VP. Conservato solo per leggere i template già salvati.
   */
  amount?: number;
}

/** Esito della selezione gettone per un evento (snapshot ledger + simulatore). */
export type CommissioningGettoneSelectionOutcome =
  /** Template senza regole: gettone gara del VP pieno (comportamento base). */
  | 'no_rules'
  /** Una regola è vera: l'evento riceve il gettone gara del VP. */
  | 'selected'
  /** Tutte le regole sono false: gettone gara 0. */
  | 'no_rule_matched'
  /** Una regola non è valutabile: fail-closed, gettone gara 0 con motivo. */
  | 'not_evaluable';

export interface CommissioningGettoneSelectionRuleTrace {
  /** Posizione della regola nel template (0-based). */
  index: number;
  label: string | null;
  functionId: string | null;
  outcome: import('./commissioning-kernel').CommissioningFunctionOutcome;
}

/** Traccia della selezione gettone: regole valutate (in ordine, fino allo stop) ed esito. */
export interface CommissioningGettoneSelectionTrace {
  outcome: CommissioningGettoneSelectionOutcome;
  /** Indice della regola che ha selezionato l'evento (null = nessuna). */
  selectedRuleIndex: number | null;
  rules: CommissioningGettoneSelectionRuleTrace[];
  /** Motivo leggibile quando il gettone gara è azzerato (null = selezionato / senza regole). */
  reason: string | null;
}
/**
 * Config specifica del tipo nel template (configurator_templates.config).
 * Le sezioni non pertinenti al tipo restano assenti.
 */
export interface CommissioningTemplateConfig {
  /** Layer principale di calcolo. */
  layer?: CommissioningInstanceScope;
  /** Tipo 'soglie': STRUTTURA delle bande (identità, nome, ordine, categorie,
   *  maggiorazioni, ammissione). I valori numerici vivono per cluster. */
  thresholds?: CommissioningThresholdBandStructure[];
  /**
   * Tipo 'soglie': dichiarazione a priori (modalità + count + fonti).
   * Presente ⇒ formato dichiarato: gli scaglioni compilano solo le fonti
   * dichiarate e il motore applica le azioni economiche per banda.
   */
  thresholdsDeclaration?: CommissioningThresholdDeclaration;
  /**
   * Paletti di sblocco (union discriminata, `kind` obbligatorio). Tutti i
   * paletti devono essere soddisfatti per sbloccare il calcolo (AND logico).
   */
  gates?: CommissioningGateV2[];
  /** Tipo 'gettone': regole di gettone flat/differenziato. */
  gettoneRules?: CommissioningGettoneRule[];
  /**
   * Tipo 'bonus_malus' («Bonus/Malus su KPI»): STRUTTURA delle regole (identità,
   * KPI, finestra, condizione, effetti dichiarati). Banda morta e importi sono
   * VALORI per livello (`declarativeThresholds.values.rules.<id>`), mai qui.
   */
  bonusMalusRules?: CommissioningBonusMalusRule[];
  /**
   * Tipo 'classifica': STRUTTURA dei criteri (metrica, verso, spareggio).
   * I partecipanti minimi sono un VALORE per livello (`values.ranking`).
   */
  rankingConfig?: CommissioningRankingConfig;
  /**
   * Tipo 'classifica': STRUTTURA delle fasce di posizione (identità,
   * etichetta, posizioni). Il premio è un VALORE per livello (`values.prizes.<id>`).
   */
  rankingPrizes?: CommissioningRankingPrize[];
  /**
   * Tipo 'aggregazione': STRUTTURA delle sorgenti ordinate (identità,
   * template). Il peso è un VALORE per livello (`values.sources.<id>`).
   */
  aggregationSources?: CommissioningAggregationSource[];
  /**
   * Tipo 'aggregazione': STRUTTURA dei livelli (identità, nome). Punteggio
   * minimo e payout sono VALORI per livello (`values.levels.<id>`).
   */
  aggregationLevels?: CommissioningAggregationLevel[];
  /** Default CAP% del template (cap_scope_filter L3 + cap_type). */
  capDefaults?: CommissioningTemplateCapDefaults;
  /** Regole di favore multipos di default del template. */
  multiposFavorRules?: CommissioningMultiposFavorRules;
  /** Default binario CONTA (solo filtro L3: la metrica è il countKind dichiarato). */
  countingDefaults?: CommissioningCountingConfig;
  /** Default binario REMUNERA (filtro L3 + fonti di pagamento dichiarate). */
  remunerationDefaults?: CommissioningRemunerationConfig;
  /**
   * Pattern BONUS/MALUS sul totale di periodo post-CAP (righe funzione →
   * ±% | ±€): in UI «Correzione sul totale di periodo». Distinto dal tipo
   * «Bonus/Malus su KPI» (`bonusMalusRules`, configuratore autonomo) e da
   * `aggregate_rules.bonusMalus` («Bonus / malus su funzioni di periodo»).
   */
  totalBonusMalus?: CommissioningTotalBonusMalusConfig;
  /** Parametri liberi addizionali per-tipo (chiave → scalare). */
  extra?: Record<string, string | number | boolean>;
}
/** Regole di favore multipos (attributo del configuratore, non del cluster). */
export interface CommissioningMultiposFavorRules {
  /** Dal N-esimo PDV in poi scattano le regole di favore (es. 3). */
  fromPdvCount: number;
  /** Riduzione percentuale del target per i PDV oltre soglia (0–100). */
  targetReductionPercent?: number;
  /** Fallback percentuale sul target (es. fallback 80%). */
  fallbackPercent?: number;
}

/** Binario CONTA del doppio binario conta/remunera. */
export interface CommissioningCountingConfig {
  /** Filtro L3 sul perimetro degli eventi che CONTANO verso soglie/target. */
  filterFunctionId?: string;
  /**
   * @deprecated Binari unificati: la metrica di count è il `countKind` della
   * dichiarazione soglie (`thresholdsDeclaration`), dichiarato UNA volta.
   * Il campo resta accettato in lettura (config salvati) ma non è mai stato
   * consumato dal runtime e non va più scritto dalla UI.
   */
  metricVariableCode?: string;
}

/** Binario REMUNERA del doppio binario conta/remunera. */
export interface CommissioningRemunerationConfig {
  /** Filtro L3 sul perimetro degli eventi REMUNERATI. */
  filterFunctionId?: string;
  /**
   * @deprecated Binari unificati: vedi CommissioningCountingConfig.
   */
  metricVariableCode?: string;
  /**
   * Fonti economiche che PAGANO il perimetro remunerato, scelte tra quelle
   * dichiarate in `thresholdsDeclaration.sources` (anche vuoto).
   * Obbligatorio nel template Soglie; [] = nessuna fonte pagante.
   * La composizione dell'importo per-evento include SOLO i contributi delle
   * fonti selezionate ("conta tutte le fibre valide, paga solo quelle con TV
   * col gettone gara").
   */
  paymentSources?: CommissioningThresholdSource[];
}

// ---------------------------------------------------------------------------
// Pattern BONUS/MALUS sul totale di periodo (riusabile tra tipi configuratore)
// ---------------------------------------------------------------------------

/** Effetto di una riga bonus/malus: ±percentuale o ±importo fisso (€). */
export interface CommissioningTotalBonusMalusEffect {
  /** 'percent' = ±% sul totale post-CAP; 'amount' = ±€ fissi. */
  kind: 'percent' | 'amount';
  /** Valore POSITIVO (il segno lo dà la lista: bonus = +, malus = −). */
  value: number;
}

/**
 * Riga del pattern bonus/malus: funzione → effetto.
 * La funzione può essere multi-condizione (builder a blocchi) e può dipendere
 * da altri configuratori via variabili cross-pista (aggregation='cross_ref').
 * Fail-closed: funzione irrisolvibile → NESSUNA applicazione (né bonus né
 * malus) con warning loggato (functionId/instanceId).
 */
export interface CommissioningTotalBonusMalusRule {
  /** Codice stabile della riga (unico nel config). */
  code: string;
  /** Etichetta leggibile opzionale. */
  label?: string;
  /** Funzione L3 gate della riga. */
  functionId: string;
  effect: CommissioningTotalBonusMalusEffect;
}

/**
 * Config del pattern bonus/malus sul TOTALE economico di periodo POST-CAP:
 * il bonus può superare il tetto del CAP, il malus lo riduce oltre (scelta
 * esplicita di dominio). Schema e componente UI condivisi, riusabili dagli
 * altri tipi di configuratore senza duplicazione.
 */
export interface CommissioningTotalBonusMalusConfig {
  bonus: CommissioningTotalBonusMalusRule[];
  malus: CommissioningTotalBonusMalusRule[];
}

/** Parametri dell'arco DAG (configurator_interactions.config). */
export interface CommissioningInteractionConfig {
  /** Quota trasferita (archi valenza/gettone), 0–100. */
  transferPercent?: number;
  /** Note operative sull'arco. */
  note?: string;
  /**
   * Task #3494/#3544 — Condizione union discriminata: funzione L3 o esito del
   * padre già calcolato in ordine topologico.
   */
  condition?: EdgeCondition;
  /**
   * Task #3494 — Fattore moltiplicativo (arco `moltiplicatore`), > 0.
   * Es. 0.7 = −30%. Richiesto quando `edge_type = 'moltiplicatore'`.
   */
  factor?: number;
  /**
   * Task #3494 — Campo target del moltiplicatore (default: `amount_eur`).
   * Solo per arco `moltiplicatore`.
   */
  target?: CommissioningMoltiplicatoreTarget;
  /**
   * Task #3494 — Verso di applicazione del moltiplicatore (default: true).
   * `true` = applica quando la condizione è vera; `false` = applica quando
   * la condizione è falsa (pattern "decurtazione su mancato raggiungimento").
   */
  applyWhenTrue?: boolean;
}

/**
 * Lato di un aggregato ratio: misura calcolata sulle righe di periodo +
 * funzione L3 opzionale valutata per-riga (solo le righe che passano
 * contribuiscono al conteggio/somma di questo lato).
 */
export interface CommissioningRatioSide {
  /** Misura aggregata applicata alle righe (eventualmente filtrate); `line_total_sum` = fatturato riga (sell-out e sell-in). */
  measure: 'line_count' | 'valenza_sum' | 'amount_sum' | 'line_total_sum';
  /** Funzione L3 opzionale di filtro per-riga. Fail-closed: funzione non risolvibile → null. */
  filterFunctionId?: string;
}

/**
 * Misura leggibile da un'altra istanza configuratore (aggregation='cross_ref').
 *
 * - valenza_sum / line_count / amount_sum: lette live dal ledger dell'istanza
 *   referenziata per lo stesso store_id dell'evento corrente (semantica per-PDV).
 *   Ammettono un filtro L3 opzionale (refFilterFunctionId) valutato riga per
 *   riga sulle righe del ledger referenziato al momento della lettura.
 * - ratio: rapporto numeratore/denominatore (numerator/denominator, stessa
 *   forma CommissioningRatioSide dei ratio sull'istanza corrente) calcolato
 *   sul ledger dell'istanza referenziata. Fail-closed: denominatore zero o
 *   filtro non valutabile → null.
 * - tgt_pct / threshold_reached / prize_earned: lette da commissioning_aggregate_results
 *   is_current=true dell'istanza referenziata; null se non ancora calcolate (fail-closed).
 * - malus_count / bonus_count / bonus_malus_sum: lette dai risultati di periodo
 *   bonus/malus CORRENTI e CONFERMATI della sorgente, sull'asse del membro
 *   (store → company → user): delta firmato per riga = amount_eur delle righe
 *   kind 'bonus'/'malus' (già delta puro) oppure amount_eur − base amount_sum
 *   delle righe kind 'bonus_malus'. malus_count = righe con delta < 0,
 *   bonus_count = righe con delta > 0, bonus_malus_sum = Σ delta.
 *   "Nessun malus nelle gare precedenti" = `malus_count = 0` con refWindow
 *   'previous_periods'.
 *
 * Ogni misura è calcolata PER PERIODO della sorgente dentro la finestra
 * `refWindow` e poi combinata con `refCombine` (unico punto di combinazione).
 */
export type CommissioningCrossRefMeasure = z.infer<typeof commissioningCrossRefMeasureSchema>;

/**
 * Finestra temporale della lettura cross_ref sulla gara SORGENTE (campo
 * distinto da `window` delle aggregazioni standard, che resta
 * period/rolling_*). Ogni voce seleziona un insieme ORDINATO di periodi della
 * sorgente; la misura è calcolata per ciascuno e combinata con `refCombine`.
 *
 * - `overlap` (default, comportamento storico): periodi della sorgente che
 *   si sovrappongono alla finestra corrente della figlia (per le misure
 *   ledger la finestra corrente è letta direttamente sul ledger, senza
 *   bisogno dei periodi della sorgente).
 * - `previous_periods`: gli ultimi `count` periodi della sorgente CONCLUSI
 *   prima dell'inizio del periodo corrente della figlia (period_end <
 *   inizio periodo corrente). Meno di `count` periodi disponibili → null
 *   fail-closed (mai un sottoinsieme implicito).
 * - `source_to_date`: tutti i periodi della sorgente iniziati entro la data
 *   di riferimento (data evento per il motore per-evento, fine del periodo
 *   corrente per gli aggregati), mai oltre oggi.
 * - `source_full`: tutti i periodi della sorgente.
 *
 * Finestra vuota → null (fail-closed) e finding preflight: mai 0 implicito.
 */
export type CommissioningCrossRefWindow =
  | { kind: 'overlap' }
  | { kind: 'previous_periods'; count: number }
  | { kind: 'source_to_date' }
  | { kind: 'source_full' };

export type CommissioningCrossRefWindowKind = CommissioningCrossRefWindow['kind'];

/**
 * Combinazione dei valori per periodo quando la finestra copre più periodi
 * della sorgente (unico punto di combinazione, condiviso da motore per-evento
 * e aggregati di periodo):
 * - sum / avg / min: misure numeriche; un periodo null → null (fail-closed);
 * - all / any: misure booleane (threshold_reached), logica tri-state Kleene
 *   (all: uno 0 → 0, altrimenti un null → null; any: un 1 → 1, altrimenti un
 *   null → null);
 * - last: valore dell'ultimo periodo della finestra (anche null).
 * Nessun periodo nella finestra → sempre null.
 */
export type CommissioningCrossRefCombine = 'sum' | 'avg' | 'min' | 'all' | 'any' | 'last';

/**
 * Riferimento cross_ref a un INTERO DRIVER DI GARA (alternativa a
 * refInstanceId): le misure vengono sommate su tutte le istanze attive della
 * gara che appartengono a quel driver (`business_driver_id`). Fail-closed:
 * nessuna istanza attiva del driver nella gara → null.
 */
export interface CommissioningCrossRefDriverRef {
  competitionId: string;
  businessDriverId: string;
}

/**
 * Bersaglio risolto di una variabile cross_ref (union discriminata):
 * - `instance`: una singola istanza configuratore (refInstanceId);
 * - `driver`: tutte le istanze attive di un driver dentro una gara (refDriver).
 */
export type CommissioningCrossRefTarget =
  | { kind: 'instance'; instanceId: string }
  | { kind: 'driver'; competitionId: string; businessDriverId: string };

/** Config del calcolo aggregato delle variabili computed (compute_config). */
export interface CommissioningVariableComputeConfig {
  /** Finestra di aggregazione. */
  window: 'period' | 'rolling_30d' | 'rolling_90d';
  /**
   * Formula/aggregatore applicato.
   * - sum/count/avg/ratio: aggregato sul ledger dell'istanza corrente.
   * - cross_ref: legge un aggregato da un'altra istanza configuratore
   *   (richiede refInstanceId + refMeasure; window ignorata per le misure aggregate).
   */
  /**
   * - sum/count/avg/ratio: aggregato sul ledger dell'istanza corrente.
   * - line_total_sum: somma dei lineTotal degli eventi del periodo
   *   (fatturato riga: sell_out e sell_in netto; eventi senza importo = 0 nel
   *   totale grezzo, non valutabili come fatto di soglia).
   * - cross_ref: legge un aggregato da un'altra istanza configuratore.
   */
  aggregation: 'sum' | 'count' | 'avg' | 'ratio' | 'line_total_sum' | 'cross_ref';
  /**
   * Lato numeratore (misura + filtro opzionale per-riga).
   * Per aggregation='ratio' (ledger istanza corrente) e per
   * aggregation='cross_ref' con refMeasure='ratio' (ledger istanza referenziata).
   */
  numerator?: CommissioningRatioSide;
  /**
   * Lato denominatore (misura + filtro opzionale per-riga).
   * Stessi contesti d'uso del numeratore.
   */
  denominator?: CommissioningRatioSide;
  /** Per sum/count/avg: filtro L3 opzionale sull'intero aggregato. */
  filterFunctionId?: string;
  /**
   * Per cross_ref: UUID dell'istanza configuratore referenziata (stesso tenant).
   * La query usa sempre store_id dell'evento corrente (semantica per-PDV);
   * se l'evento non ha store_id (es. scope RS), fallback su company_id,
   * poi user_id, poi totale istanza senza filtro asse — documentato nel runtime.
   */
  refInstanceId?: string;
  /**
   * Per cross_ref: riferimento a un intero DRIVER DI GARA, alternativo e
   * mutuamente esclusivo con refInstanceId. Misure ammesse: line_count,
   * valenza_sum, amount_sum, ratio, tgt_pct (Σ valenza / Σ TGT risolto per
   * membro×driver delle istanze del driver). Stesso asse per-PDV.
   */
  refDriver?: CommissioningCrossRefDriverRef;
  /** Per cross_ref: misura da leggere dall'istanza referenziata. */
  refMeasure?: CommissioningCrossRefMeasure;
  /**
   * Per cross_ref con misura ledger (valenza_sum/line_count/amount_sum):
   * funzione L3 opzionale di filtro valutata riga per riga sul ledger
   * dell'istanza referenziata (solo alla lettura: il driver referenziato
   * non viene toccato). Fail-closed: funzione non risolvibile → null.
   * Non ammesso per le misure aggregate né per refMeasure='ratio'
   * (il ratio porta i filtri dentro i lati numerator/denominator).
   */
  refFilterFunctionId?: string;
  /**
   * Per cross_ref: finestra sui periodi della gara SORGENTE (vedi
   * CommissioningCrossRefWindow). Assente = `{ kind: 'overlap' }` (storico).
   * NON confondere con `window` (period/rolling_*) delle aggregazioni standard.
   */
  refWindow?: CommissioningCrossRefWindow;
  /**
   * Per cross_ref: come combinare i valori per periodo quando la finestra
   * copre più periodi della sorgente. Assente = default per misura
   * (resolveCommissioningCrossRefCombine): threshold_reached → any,
   * tgt_pct → last, tutte le altre → sum (riproduce il comportamento storico).
   */
  refCombine?: CommissioningCrossRefCombine;
}

// ---------------------------------------------------------------------------
// Righe entità (shape DB → FE, senza `any`)
// ---------------------------------------------------------------------------

export interface CommissioningVariableCategory {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  code: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommissioningVariable {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  category_id: string | null;
  code: string;
  name: string;
  description: string | null;
  resolver_key: string;
  /** Ampliato ai 10 tipi kernel v2 (Task #3453); i 6 legacy restano validi. */
  data_type: import('./commissioning-kernel').CommissioningKernelDataType;
  is_computed: boolean;
  compute_config: CommissioningVariableComputeConfig | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
  // --- Metadata kernel v2 (Task #3453) ---
  source_kind: import('./commissioning-kernel').CommissioningVariableSourceKind;
  subject_kind: import('./commissioning-kernel').CommissioningVariableSubject;
  min_phase: import('./commissioning-kernel').CommissioningVariablePhase;
  cardinality: import('./commissioning-kernel').CommissioningVariableCardinality;
  family_coverage: ReadonlyArray<CommissioningEventSourceType>;
  wiring_status: import('./commissioning-kernel').CommissioningWiringStatus;
  missing_policy: import('./commissioning-kernel').CommissioningMissingPolicy;
  unit: string | null;
  value_precision: number | null;
  depends_on: ReadonlyArray<string>;
  perimeter: import('./commissioning-kernel').CommissioningVariablePerimeter | null;
  version_group_id: string;
  version: number;
  is_current_version: boolean;
  archived_at: string | null;
  /**
   * Fonte valori ammessi per tipi single_select/multi_select.
   * null = nessuna sorgente dichiarata (valore libero lato UI).
   */
  select_source: import('./commissioning-kernel').CommissioningVariableSelectSource | null;
  created_at: string;
  updated_at: string;
  /**
   * Valori ammessi per variabili `single_select` con dominio chiuso.
   * Iniettato dal backend via lookup del contratto resolver (`allowedValues`);
   * assente per domini aperti o per tipi diversi da `single_select`.
   * Non è una colonna DB — viene calcolato lato service al momento del list/get.
   */
  allowed_values?: ReadonlyArray<string>;
}

export interface CommissioningReferenceList {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  /** Audit archiviazione (Task #3450): valorizzati quando is_active=false. */
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommissioningReferenceListValue {
  id: string;
  tenant_id: string;
  list_id: string;
  value: string;
  label: string | null;
  sort_order: number;
  is_active: boolean;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Task #3571 — Snapshot content-addressed di una versione di lista di
 * riferimento. Una riga per versione di contenuto (hash dei soli value attivi
 * ordinati). Append-only: nessun UPDATE, solo SELECT/INSERT lato app.
 */
export interface CommissioningReferenceListSnapshot {
  id: string;
  tenant_id: string;
  list_id: string;
  /** sha256 hex (64 char) del contenuto canonico (solo value attivi ordinati). */
  content_hash: string;
  /** Array ordinato dei value attivi al momento dello snapshot. */
  values: string[];
  created_at: string;
}
export interface CommissioningFunction {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  name: string;
  description: string;
  condition_tree: import('./commissioning-kernel').CommissioningKernelNode;
  version: number;
  is_active: boolean;
  // --- Kernel v2 (Task #3453): governance pubblicazione ---
  status: import('./commissioning-kernel').CommissioningPublicationStatus;
  valid_from: string | null;
  valid_to: string | null;
  pinned_bundle: import('./commissioning-kernel').CommissioningFunctionPinnedBundle | null;
  created_at: string;
  updated_at: string;
}

export interface CommissioningCluster {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  name: string;
  description: string | null;
  entity_type: CommissioningClusterEntityType;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Voce della libreria cluster modello (GET /incentivazione/clusters):
 * conteggio membri attivi + nome dell'organizzazione insegna che lo ha
 * pushato (null = cluster proprio del tenant).
 */
export interface CommissioningClusterListItem extends CommissioningCluster {
  member_count: number;
  managed_by_org_name: string | null;
}

export interface CommissioningClusterMember {
  id: string;
  tenant_id: string;
  cluster_id: string;
  company_id: string | null;
  store_id: string | null;
  user_id: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}
/** Campi comuni a tutte le famiglie di gara. */
export interface CommissioningCompetitionBase {
  id: string;
  tenant_id: string;
  /** Source admission only; not the participant/economic-context version.
   * Stored invariant (always 2, enforced by the database CHECK), never a
   * discriminator: the declared source of an instance must belong to the
   * family of the competition, with or without a VP. */
  event_admission_version: 2;
  managed_by_org_id: string | null;
  name: string;
  description: string | null;
  /**
   * Fornitore della testata (`pos_suppliers`, chi emette il DDT — NON il
   * marchio prodotto né l'insegna). Identità = parametro, non funzione:
   * `null` = qualunque fornitore; valorizzato = ammessi solo gli eventi del
   * fornitore (sell-in: fornitore documento; sell-out: fornitore prodotto),
   * evento senza il dato = fuori. Facoltativo per tutte le famiglie.
   */
  supplier_id: string | null;
  period_start: string;
  period_end: string;
  status: CommissioningCompetitionStatus;
  letter_r2_key: string | null;
  created_at: string;
  updated_at: string;
}

/** Gara insegna (sell-out): insegna + canale OBBLIGATORI. */
export type CommissioningCompetitionInsegna = CommissioningCompetitionBase & CommissioningCommonEconomy & {
  family: 'insegna';
  retail_brand_id: string;
  channel_id: string;
};

/**
 * Gara sell-in (ordini/carichi fornitore): insegna + canale OBBLIGATORI
 * (Task #3484). Record legacy pre-vincolo possono ancora arrivare con campi
 * NULL dal DB: il preflight di pubblicazione li blocca, qui il tipo resta
 * nullable per riflettere la realtà dei dati storici in lettura.
 */
export type CommissioningCompetitionSellin = CommissioningCompetitionBase & CommissioningCommonEconomy & {
  family: 'sellin';
  retail_brand_id: string | null;
  channel_id: string | null;
};

/** Gara risorse interne (beneficiario = utente): insegna/canale MAI salvati. */
export type CommissioningCompetitionRisorse = CommissioningCompetitionBase & CommissioningCommonEconomy & {
  family: 'risorse';
  retail_brand_id: null;
  channel_id: null;
};

/** Union discriminata per famiglia. */
export type CommissioningCompetition =
  | CommissioningCompetitionInsegna
  | CommissioningCompetitionSellin
  | CommissioningCompetitionRisorse;

export interface CommissioningCompetitionAcknowledgment {
  id: string;
  tenant_id: string;
  competition_id: string;
  user_id: string;
  action: CommissioningAckAction;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface CommissioningConfiguratorType {
  id: string;
  code: CommissioningConfiguratorTypeCode;
  name: string;
  description: string;
  ui_component: string;
  wizard_steps: CommissioningWizardStep[];
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommissioningConfiguratorTemplate {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  type_id: string;
  name: string;
  description: string;
  config: CommissioningTemplateConfig;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Abbinamento configuratore × cluster i cui valori Soglie non coprono più la struttura del template. */
export interface CommissioningTemplateIncompleteAssignment {
  instance_id: string;
  instance_name: string;
  competition_id: string;
  competition_name: string;
  competition_cluster_id: string;
  competition_cluster_name: string;
}

/** Risposta dell'aggiornamento template: il template salvato più l'impatto sui cluster abbinati. */
export interface CommissioningConfiguratorTemplateUpdateResult extends CommissioningConfiguratorTemplate {
  /** Cluster che, con la nuova struttura, risultano "Valori mancanti" (i valori ancora validi restano). */
  incomplete_assignments: CommissioningTemplateIncompleteAssignment[];
}

export interface CommissioningConfiguratorInstance extends CommissioningInstanceThresholdMetadata {
  id: string;

  tenant_id: string;

  competition_id: string;

  template_id: string;

  name: string;

  scope: CommissioningInstanceScope;
  /**
   * Famiglia evento dichiarata dall'istanza (Task #3465): obbligatoria quando
   * è agganciato un value package; il motore filtra prima di L2.
   */

  period_start: string | null;

  period_end: string | null;

  multipos_favor_rules: CommissioningMultiposFavorRules | null;

  cap_scope_filter_function_id: string | null;

  cap_type: CommissioningCapType | null;

  cap_value: number | null;
  /** Task CAP evoluto — metrica del tetto (null = amount_eur, storico). */

  cap_metric: CommissioningCapMetric | null;
  /** Task CAP evoluto — variabile L1 base della % (null = auto-referenziale legacy). */

  cap_base_variable_code: string | null;

  counting_config: CommissioningCountingConfig;

  remuneration_config: CommissioningRemunerationConfig;
  /** Regole aggregate v3 (soglie/ranking/checkpoint/bonus-malus) dell'istanza. */

  aggregate_rules: import('./commissioning-runtime-v3').CommissioningAggregateRules | null;
  /** Progressivo delle pubblicazioni regole (snapshot in instance_rule_versions). */

  rules_version: number;

  is_active: boolean;

  created_at: string;

  updated_at: string;

  event_family: CommissioningVpEventFamily | null;
  /**
   * Driver di gara a cui appartiene l'istanza (gara → driver → istanze).
   * NULL = istanza EXTRA-DRIVER (livello gara): lavora sulla risultante delle
   * altre istanze; consuma eventi solo se dichiara `source_driver_ids`.
   */
  business_driver_id: string | null;
  /**
   * Driver SORGENTE di un'istanza extra-driver (sempre [] se
   * `business_driver_id` è valorizzato). Vuoto = nessun evento consumato
   * (solo regole di periodo); valorizzato = consuma riga per riga gli eventi di
   * quei driver, con TGT per membro×driver sorgente.
   */
  source_driver_ids: string[];
  /**
   * VP del configuratore, scelto fra i VP della gara: tutti i cluster e le
   * celle driver lo ereditano. NULL ammesso solo per i configuratori che non
   * usano fonti valenza/canone/gettone (`commissioningInstanceRequiresValuePackage`).
   */
  value_package_id: string | null;
  /**
   * Perimetro prodotto del configuratore (marchi/liste modelli/listini/
   * categorie/tipologie con binario per dimensione), applicato in ammissione
   * prima di ogni funzione; null = nessun filtro.
   */
  perimeter: import('../schemas/commissioning-instance-perimeter').CommissioningInstancePerimeter | null;
}

/**
 * Driver di business ammesso da una gara per le sue istanze configuratore
 * (`GET /incentivazione/competitions/:id/eligible-drivers`): driver attivo e
 * dell'insegna/canale della gara oppure trasversale (senza insegna); per la
 * famiglia `risorse` qualunque driver attivo.
 */
export interface CommissioningEligibleDriver {
  id: string;
  name: string;
  slug: string | null;
  retail_brand_id: string | null;
  channel_id: string | null;
  /** true = trasversale (nessuna insegna). */
  is_transversal: boolean;
}

export interface CommissioningConfiguratorInteraction {
  id: string;
  tenant_id: string;
  source_instance_id: string;
  target_instance_id: string;
  edge_type: CommissioningInteractionEdgeType;
  config: CommissioningInteractionConfig;
  created_at: string;
  updated_at: string;
}

export interface CommissioningValuePackage {
  id: string;

  tenant_id: string;

  managed_by_org_id: string | null;

  name: string;

  description: string | null;
  /** Famiglia evento del package: gate autoritativo pre-L2 nel motore. */

  valid_from: string | null;

  valid_to: string | null;

  is_active: boolean;
  /** Catena di versioni (decorrenze in corso gara): gruppo di appartenenza. */

  version_group_id: string;
  /** Progressivo versione nel gruppo (1 = capostipite). */

  version: number;
  /** Testa della catena (unica per gruppo): l'unica versione editabile. */

  is_current_version: boolean;
  /** Archiviazione soft dell'intero gruppo (null = vivo). */

  archived_at: string | null;

  created_at: string;

  updated_at: string;

  event_family: CommissioningVpEventFamily;
  /**
   * Pubblicazione della versione: congela la fotografia piatta
   * product_id → valori e gli scenari (immutabili a DB). null = bozza.
   */

  published_at: string | null;

  /** Ultimo aggiornamento analisi salute (Stage D). null = mai analizzato. */
  last_analysis_at: string | null;

  /**
   * Stato salute più recente: 'aligned' | 'review' | 'blocking'.
   * null = mai analizzato.
   */
  analysis_health: 'aligned' | 'review' | 'blocking' | null;

  /**
   * Task #3559 — Sostituzione da correzione ("Modifica versione in corso").
   * Quando una correzione viene pubblicata, la versione corretta è marcata
   * sostituita (esclusa dal resolver as-of del motore). null = versione viva.
   */
  superseded_at: string | null;
  /** La versione (correzione promossa) che ha sostituito questa. */
  superseded_by_version_id: string | null;
  /** Utente che ha pubblicato la correzione (senza FK: può essere un super_admin master). */
  superseded_by_user_id: string | null;
  /**
   * Se valorizzato: la riga è nata come correzione della versione indicata.
   * Bozza di correzione pendente = correction_of_version_id NOT NULL AND
   * published_at IS NULL. Resta valorizzato dopo la promozione (traccia).
   */
  correction_of_version_id: string | null;
}

/**
 * Base economica piatta fotografata per prodotto:
 * commissioning_value_package_bases. Il fallback runtime, in assenza di
 * scenari applicabili, è ESATTAMENTE questa base; nessuna ereditarietà
 * economica da filtri o tassonomie.
 */
export interface CommissioningVpProductBase {
  id: string;
  tenant_id: string;
  package_id: string;
  product_id: string;
  /** Snapshot descrittivo alla fotografia (SoT resta pos_products). */
  product_name: string;
  /** Fonte/catalogo di riferimento alla selezione (listino canvas, se noto). */
  source_price_list_id: string | null;
  source_price_list_name: string | null;
  valenza: number;
  gettone_contrattuale: number;
  gettone_gara: number;
  canone_multiplier: number | null;
  // Stage F: canone_inherited e canone_override rimossi dalla migrazione
  // 20261008150000_drop_canone_legacy_columns.sql — rimossi anche dal tipo.
  /**
   * Task #3482 — monthly_fee fotografato dal listino canvas al momento
   * dell'inserimento. Immutabile dopo la fotografia. NULL per basi storiche o
   * prodotti senza listino canvas (UI: "—" / badge "non fotografato").
   */
  canone_base_offerta: number | null;
  /**
   * Task #3482 — importo contrattuale di gara, modificabile in bozza.
   * Fotografato = canone_base_offerta alla selezione; poi gestito autonomamente.
   */
  canone_gara: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Task #3537 — Base prodotto arricchita per il dettaglio VP (datatable editor):
 * campi descrittivi risolti LIVE con LEFT JOIN su pos_products / pos_categories /
 * pos_price_lists / pos_suppliers. Nullable: prodotti/listini rimossi o storici
 * non rompono la lettura (UI: "—").
 */
export interface CommissioningVpProductBaseDetail extends CommissioningVpProductBase {
  /** Fornitore del listino sorgente (via pos_price_lists.supplier_id). */
  supplier_id: string | null;
  supplier_name: string | null;
  /** Categoria corrente del prodotto (SoT pos_products, non fotografata). */
  category_id: string | null;
  category_name: string | null;
  /** Natura corrente del prodotto ('canvas' | 'product' | 'service'). */
  product_nature: string | null;
}
/** Parametro commerciale del driver (commissioning_addon_parameters). */
export interface CommissioningAddonParameter {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  business_driver_id: string;
  /** Chiave canonica stabile (snake_case), consumata dal commissioning. */
  code: string;
  question: string;
  description: string | null;
  info_category: string | null;
  value_type: CommissioningAddonParamType;
  /** Lista OBBLIGATORIA per single/multi select; null per gli altri tipi. */
  list_id: string | null;
  /** true dopo la prima pubblicazione: driver/code/tipo/lista/domanda immutabili. */
  is_adopted: boolean;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Valore ammesso del parametro (join su commissioning_reference_list_values). */
export interface CommissioningAddonParameterAllowedValue {
  id: string;
  list_value_id: string;
  sort_order: number;
  /** Denormalizzati in lettura dal valore di lista (SoT resta la lista). */
  value_code: string;
  label: string | null;
  value_archived_at: string | null;
}

/** Riga elenco parametri (vista Parametri: usage + criticità). */
export interface CommissioningAddonParameterListRow extends CommissioningAddonParameter {
  list_code: string | null;
  list_name: string | null;
  /** N. add-on (identità distinte) che compongono questo parametro. */
  addon_usage_count: number;
  allowed_value_count: number;
}

/** Dettaglio parametro: valori ammessi + add-on utilizzatori. */
export interface CommissioningAddonParameterDetail extends CommissioningAddonParameterListRow {
  allowed_values: CommissioningAddonParameterAllowedValue[];
  used_by_addons: Array<{
    addon_id: string;
    addon_name: string;
    version: number;
    version_status: CommissioningAddonVersionStatus;
  }>;
}

/** Identità stabile dell'add-on (commissioning_addons, ristrutturata). */
export interface CommissioningAddon {
  id: string;
  tenant_id: string;
  managed_by_org_id: string | null;
  name: string;
  description: string | null;
  business_driver_id: string;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Versione dell'add-on (immutabile dopo la pubblicazione). */
export interface CommissioningAddonVersion {
  id: string;
  tenant_id: string;
  addon_id: string;
  version: number;
  status: CommissioningAddonVersionStatus;
  /** Decorrenza: risolta per data operazione (max valid_from <= data). */
  valid_from: string;
  published_at: string | null;
  published_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/** Riga di applicabilità a matrioska della versione (tutte NULL = intero driver). */
export interface CommissioningAddonVersionScope {
  id: string;
  category_id: string | null;
  product_type_id: string | null;
  product_id: string | null;
  /** Denormalizzati in lettura per la UI. */
  category_name: string | null;
  product_type_name: string | null;
  product_name: string | null;
}

/** Utilizzo di un parametro dentro una versione (regole di compilazione). */
export interface CommissioningAddonVersionParameter {
  id: string;
  parameter_id: string;
  is_required: boolean;
  sort_order: number;
  help_text: string | null;
  min_selections: number | null;
  max_selections: number | null;
  /** Denormalizzati in lettura dal parametro (SoT resta il parametro). */
  parameter_code: string;
  question: string;
  value_type: CommissioningAddonParamType;
  list_id: string | null;
  parameter_archived_at: string | null;
}

/** Riga elenco add-on (vista Add-on del workspace). */
export interface CommissioningAddonListRow extends CommissioningAddon {
  business_driver_name: string | null;
  /** Testa corrente (ultima pubblicata per decorrenza) e bozza eventuale. */
  current_version: number | null;
  current_version_id: string | null;
  current_valid_from: string | null;
  draft_version_id: string | null;
  version_count: number;
  /** Livello più specifico fra gli scope della versione di riferimento. */
  most_specific_level: CommissioningAddonScopeLevel | null;
  /** N. prodotti coinvolti dalla versione di riferimento. */
  covered_product_count: number;
  parameter_count: number;
}

/** Dettaglio completo di una versione (editor + diff). */
export interface CommissioningAddonVersionDetail extends CommissioningAddonVersion {
  scopes: CommissioningAddonVersionScope[];
  parameters: CommissioningAddonVersionParameter[];
}

/** Dettaglio add-on: identità + versioni (sommario) + versione richiesta. */
export interface CommissioningAddonDetail extends CommissioningAddon {
  business_driver_name: string | null;
  versions: CommissioningAddonVersion[];
}

/** Diff semantico fra due versioni (BE-computed, consumato dal publish). */
export interface CommissioningAddonVersionDiff {
  previous_version: number | null;
  valid_from: { previous: string | null; next: string };
  scopes_added: CommissioningAddonVersionScope[];
  scopes_removed: CommissioningAddonVersionScope[];
  parameters_added: CommissioningAddonVersionParameter[];
  parameters_removed: CommissioningAddonVersionParameter[];
  parameters_changed: Array<{
    parameter_code: string;
    changes: Array<{
      field: 'is_required' | 'sort_order' | 'help_text' | 'min_selections' | 'max_selections';
      previous: string | number | boolean | null;
      next: string | number | boolean | null;
    }>;
  }>;
}

// ---------------------------------------------------------------------------
// Questionario risolto (renderer UNICO Catalogo/POS) e risposte in vendita
// ---------------------------------------------------------------------------

/** Opzione selezionabile (fotografabile nello snapshot di vendita). */
export interface CommissioningAddonQuestionOption {
  value_id: string;
  code: string;
  label: string;
}

/** Origine deterministica di una domanda risolta (perché appare). */
export interface CommissioningAddonQuestionOrigin {
  addon_id: string;
  addon_name: string;
  version_id: string;
  version: number;
  valid_from: string;
  scope_level: CommissioningAddonScopeLevel;
}

/** Domanda del questionario risolto per prodotto+data. */
export interface CommissioningResolvedAddonQuestion {
  parameter_id: string;
  parameter_code: string;
  question: string;
  description: string | null;
  info_category: string | null;
  value_type: CommissioningAddonParamType;
  is_required: boolean;
  sort_order: number;
  help_text: string | null;
  min_selections: number | null;
  max_selections: number | null;
  options: CommissioningAddonQuestionOption[];
  /** Add-on/versione/livello vincenti (config più specifica). */
  origin: CommissioningAddonQuestionOrigin;
  /** Configurazioni concorrenti alla stessa specificità con regole DIVERSE. */
  conflict: { competing: CommissioningAddonQuestionOrigin[] } | null;
}

/** Questionario deterministico risolto per un prodotto a una data. */
export interface CommissioningResolvedAddonQuestionnaire {
  product_id: string;
  resolved_at_date: string;
  addons: CommissioningAddonQuestionOrigin[];
  questions: CommissioningResolvedAddonQuestion[];
}

/** Risposta fotografata sulla riga vendita (commissioning_sale_addon_responses). */
export interface CommissioningSaleAddonResponse {
  id: string;
  tenant_id: string;
  sale_id: string;
  sale_detail_id: string;
  addon_id: string;
  addon_version_id: string;
  parameter_id: string;
  addon_name: string;
  addon_version: number;
  parameter_code: string;
  question: string;
  value_type: CommissioningAddonParamType;
  is_required: boolean;
  options_snapshot: CommissioningAddonQuestionOption[];
  answer_state: CommissioningAddonAnswerState;
  answer: CommissioningAddonAnswerSnapshot | null;
  source: CommissioningAddonAnswerSource;
  raw_value: string | null;
  confidence: number | null;
  confirmed: boolean;
  created_at: string;
}

/**
 * Snapshot della risposta persistito in vendita: valore canonico + etichette
 * congelate (per i select), autosufficiente per audit e ricalcoli storici.
 */
export type CommissioningAddonAnswerSnapshot =
  | { type: 'boolean'; value: boolean }
  | { type: 'number'; value: number }
  | { type: 'text'; value: string }
  | { type: 'single_select'; value_id: string; code: string; label: string }
  | {
      type: 'multi_select';
      selections: Array<{ value_id: string; code: string; label: string }>;
    };

/** Sorgente dell'evento normalizzato (CHECK su commissioning_events.source_type). */
export const COMMISSIONING_EVENT_SOURCE_TYPES = ['sell_out', 'crm', 'sell_in'] as const;

/** RuntimeContext per evento sell-in. */
export interface CommissioningRuntimeContextSellIn extends CommissioningRuntimeContextBase {
  kind: 'sell_in';
  sellin: CommissioningRuntimeNamespace;
  product: CommissioningRuntimeNamespace;
}

/** Job: processa un evento normalizzato attraverso la pipeline L1→L3→L2→ledger. */
export interface CommissioningProcessEventJobData {
  type: 'process-event';
  tenantId: string;
  eventId: string;
}

/** Payload evento sell-out (una riga vendita POS = un evento). */
export interface CommissioningSellOutEventPayload {
  kind: 'sell_out';
  saleId: string;
  saleDetailId: string | null;
  saleNumber: string | null;
  productId: string | null;
  productName: string | null;
  categoryId: string | null;
  productTypeId: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  priceListId: string | null;
  priceListName: string | null;
  /** Canone mensile del listino al momento della vendita (congelato). */
  monthlyFee: number | null;
  /** Sconto % applicato alla riga (Task #3457, colonna pos_sale_details). */
  discountPercent: number | null;
  /** Entry fee del canvas item al momento della vendita (Task #3457). */
  entryFee: number | null;
  /** Durata contrattuale mesi del canvas item (Task #3457). */
  contractDurationMonths: number | null;
  paymentMethodCode: string | null;
  /**
   * Task #4322 — struttura di credito SNAPSHOT immutabile dalla riga di vendita
   * (`pos_sale_details.*`, cristallizzata dal pricing loop): modalità di
   * pagamento all'insegna (code), rimborso rate, ente, finanziato
   * unitario, numero rate. null = assente (fail-closed nel kernel, mai default).
   * Il titolare del credito NON viaggia nell'evento: resta snapshot
   * amministrativo sulla riga (`pos_sale_details.credit_holder`), non è un
   * criterio di gara.
   */
  paymentModeCode: string | null;
  /** @deprecated payload storici: titolare credito, ignorato dal runtime; mai scritto. */
  creditHolder?: PaymentModeCreditHolder | null;
  repaymentMethod: InstallmentRepayment | null;
  financingPartnerId: string | null;
  financedAmount: number | null;
  installmentsCount: number | null;
  /** @deprecated payload storici pre-snapshot credito (≡ repaymentMethod); mai scritto. */
  installmentMethod?: InstallmentRepayment | null;
  /**
   * Margine economico riga (line_total − purchase_price_unit × quantity).
   * null/assente se purchase_price_unit non valorizzato (es. prodotti non in
   * regime margine IVA art.36): fail-closed nel kernel → missing_policy.
   * Opzionale per retrocompatibilità con payload storici (assente ≡ null).
   */
  margin?: number | null;
  customerId: string | null;
  /**
   * Task #4332 — natura anagrafica del prodotto della riga
   * (pos_products.product_nature). Opzionale nei payload storici (≡ null).
   */
  productNature?: ProductNature | null;
  /**
   * Busta eventi gara — fatti di identità FOTOGRAFATI all'emissione (nessun
   * lookup live nelle regole). Tutti opzionali e nullable: nei payload storici
   * sono assenti (≡ null → `missing` nel kernel, mai default); nessun backfill.
   * I campi "raw" (prodotto, listino) restano distinti dai campi "risolti"
   * (`saleRetailBrandId`, `saleChannelId`) così le regole possono scegliere la fonte.
   */
  /**
   * Fonte: `pos_products.supplier_id` (fornitore anagrafico del prodotto).
   * È anche il fatto della busta per il gate fornitore della testata gara
   * (sell-out): con testata valorizzata e fatto assente l'evento è
   * `not_applicable`, mai ammesso per difetto.
   */
  productSupplierId?: string | null;
  /** Fonte: `pos_products.brand_id` → `pos_brands` (MARCHIO, es. Xiaomi/TCL; NON l'insegna). */
  productBrandId?: string | null;
  /** Fonte: `pos_products.retail_brand_id` (INSEGNA del prodotto). */
  productRetailBrandId?: string | null;
  /** Fonte: `pos_price_lists.retail_brand_id` del listino della riga. */
  priceListRetailBrandId?: string | null;
  /** Fonte: `pos_price_lists.supplier_id` del listino della riga. */
  priceListSupplierId?: string | null;
  /** Fonte: `pos_price_lists.list_type` del listino della riga. */
  priceListType?: PriceListType | null;
  /**
   * Fonte: TUTTI i canali in `pos_price_list_channels` del listino della riga,
   * ordinati deterministicamente (ORDER BY in acquisizione). Listino assente o
   * senza canali → null (mai `[]`).
   */
  priceListChannelIds?: string[] | null;
  /**
   * Insegna RISOLTA della riga. Precedenza: snapshot canvas della riga
   * (`pos_sale_detail_canvas_snapshots.retail_brand_id`) → insegna del listino
   * della riga → insegna del prodotto; altrimenti null.
   */
  saleRetailBrandId?: string | null;
  /**
   * Canale RISOLTO della riga. Precedenza: snapshot canvas della riga
   * (`channel_id`) → canale che la sede della vendita ha per l'insegna risolta
   * (`store_brands(store_id, brand_id).channel_id`); insegna null o sede senza
   * quell'insegna → null.
   */
  saleChannelId?: string | null;
  /** Composizione bundle (se la riga è un componente di composizione). */
  composition: {
    /**
     * @deprecated nome storico: contiene l'ID della COMPOSIZIONE (non del
     * componente). Mantenuto per i payload già persistiti; usare `compositionId`.
     */
    compositionProductId: string;
    /** ID della composizione selezionata (Task #4332, alias chiarito). */
    compositionId?: string;
    componentCount: number;
    /** Tipi prodotto dei componenti, ordinati deterministicamente (ORDER BY). */
    componentProductTypeIds: string[];
    /** Task #4332 — prodotti componenti (set ordinato per nome prodotto, id). */
    componentProductIds?: string[];
    /** Task #4332 — nature distinte dei componenti (set ordinato). */
    componentNatures?: ProductNature[];
    /** Task #4332 — la composizione include un canvass (canvas_item_id). */
    hasCanvass?: boolean;
    /** Task #4332 — prodotto del canvass della composizione (null se assente). */
    canvassProductId?: string | null;
  } | null;
  addons: CommissioningEventAddonSnapshot[];
}

export type CommissioningEventStatus = typeof COMMISSIONING_EVENT_STATUSES[number];

/** Namespace del RuntimeContext: mappa chiave → scalare (zero `any`). */
export type CommissioningRuntimeNamespace = Record<string, CommissioningRuntimeScalar>;

/** Payload evento CRM (trigger gated su campagna con driver). */
export interface CommissioningCrmEventPayload {
  kind: 'crm';
  trigger: CommissioningCrmTrigger;
  /** Validated immutable source, absent on historical event payloads. */
  occurrence?: import('../schemas/commissioning-crm-occurrence').CommissioningCrmOccurrenceReference;
  campaignId: string;
  campaignName: string | null;
  leadId: string | null;
  customerId: string | null;
  personId: string | null;
  stageId: string | null;
  formId: string | null;
  appointmentId: string | null;
  massActionId: string | null;
}

/** Payload evento sell-in (carichi/ordini fornitore, doppio cut-off). */
export interface CommissioningSellInEventPayload {
  kind: 'sell_in';
  documentType: 'ddt_passivo' | 'purchase_order';
  documentId: string;
  documentNumber: string | null;
  /** Data documento = competenza (cut-off 1). */
  documentDate: string | null;
  /** Data ricezione/consuntivazione (cut-off 2). */
  receivedAt: string;
  supplierId: string | null;
  supplierName: string | null;
  warehouseId: string | null;
  productId: string | null;
  productName: string | null;
  quantity: number;
  /** Prezzo unitario di riga; `null` = prezzo assente sul documento (mai 0 inventato). */
  unitCost: number | null;
  /** Totale riga NETTO = quantità × prezzo × (1 − sconto%/100); `null` se il prezzo è assente.
   * Eventi storici (pre-netto): lordo, senza `discountPercent`/`grossLineTotal`. */
  lineTotal: number | null;
  /**
   * Busta eventi gara — fatti del prodotto FOTOGRAFATI all'emissione (nessun
   * lookup live nelle regole). Tutti opzionali e nullable: nei payload storici
   * sono assenti (≡ null → `missing` nel kernel, mai default); nessun backfill.
   * `supplierId` resta il fornitore del DOCUMENTO e non viene sovrascritto.
   */
  /** Fonte: `pos_products.category_id` (categoria diretta, nessun fallback sul padre). */
  categoryId?: string | null;
  /** Sconto % di riga del documento (0 se non scontata); opzionale nei payload storici (≡ null). */
  discountPercent?: number | null;
  /** Totale riga LORDO = quantità × prezzo; `null` se il prezzo è assente; opzionale nei payload storici (≡ null). */
  grossLineTotal?: number | null;
  /** Fonte: `pos_products.product_type_id`. */
  productTypeId?: string | null;
  /** Fonte: `pos_products.product_nature`. */
  productNature?: ProductNature | null;
  /** Fonte: `pos_products.brand_id` → `pos_brands` (MARCHIO; NON l'insegna). */
  productBrandId?: string | null;
  /** Fonte: `pos_products.supplier_id` (fornitore anagrafico del prodotto, ≠ fornitore del documento). */
  productSupplierId?: string | null;
  /** Fonte: `pos_products.retail_brand_id` (INSEGNA del prodotto). */
  productRetailBrandId?: string | null;
  /** Insegna RISOLTA della riga = insegna del prodotto; altrimenti null. */
  retailBrandId?: string | null;
  /**
   * Canale RISOLTO della riga = `store_brands(store_id, brand_id).channel_id`
   * della sede del magazzino del documento per l'insegna risolta; magazzino
   * senza sede, insegna null o sede senza quell'insegna → null.
   */
  channelId?: string | null;
}

export type CommissioningEventSourceType = typeof COMMISSIONING_EVENT_SOURCE_TYPES[number];

/** Union discriminata dei payload evento per source_type. */
export type CommissioningEventPayload =
  | CommissioningSellOutEventPayload
  | CommissioningCrmEventPayload
  | CommissioningSellInEventPayload;

/** Riga commissioning_events (shape DB → FE). */
export interface CommissioningEvent {
  id: string;
  tenant_id: string;
  source_type: CommissioningEventSourceType;
  source_event_key: string;
  source_ref_type: string;
  source_ref_id: string | null;
  business_driver_id: string;
  store_id: string | null;
  company_id: string | null;
  user_id: string | null;
  occurred_at: string;
  recorded_at: string;
  payload: CommissioningEventPayload;
  status: CommissioningEventStatus;
  reversal_of_event_id: string | null;
  error_message: string | null;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Union discriminata del RuntimeContext (zero `any`). */
export type CommissioningRuntimeContext =
  | CommissioningRuntimeContextSellOut
  | CommissioningRuntimeContextCrm
  | CommissioningRuntimeContextSellIn;

/** Esito applicazione di un arco DAG sulla riga (tracciato nello snapshot). */
export interface CommissioningLedgerDagEffect {
  edgeId: string;
  edgeType: CommissioningInteractionEdgeType;
  sourceInstanceId: string;
  targetInstanceId: string;
  transferPercent: number | null;
  applied: boolean;
  reason: string | null;
  /**
   * Task #3494 — Esito della valutazione della condizione arco:
   * `true`/`false` = condizione valutata; `null` = non valutabile
   * (conservativo); `undefined` = nessuna condizione presente.
   */
  conditionOutcome?: boolean | null;
  /**
   * Task #3494 — Metrica letta dal padre (solo `parent_outcome`).
   * Assente per condizioni L3 o archi senza condizione.
   */
  parentMetric?: CommissioningEdgeConditionMetric | null;
  /** Task #3494 — Valore numerico della metrica del padre (solo `parent_outcome`). */
  parentMetricValue?: number | null;
  /** Task #3494 — Fattore applicato (solo arco `moltiplicatore`). */
  factor?: number | null;
  /** Task #3494 — Campo scalato (solo arco `moltiplicatore`). */
  moltiplicatoreTarget?: CommissioningMoltiplicatoreTarget | null;
}

/** Delta di una revisione riga: { before, after } dei campi cambiati. */
export interface CommissioningEventLineRevisionDelta {
  before: Partial<Pick<CommissioningEventLine,
    'valenza' | 'gettone_contrattuale' | 'gettone_gara' | 'amount_eur' | 'status'>>;
  after: Partial<Pick<CommissioningEventLine,
    'valenza' | 'gettone_contrattuale' | 'gettone_gara' | 'amount_eur' | 'status'>>;
}

/** CAP applicato alla riga (tracciato nello snapshot). */
export interface CommissioningLedgerCapEffect {
  capType: CommissioningCapType;
  capValue: number;
  scopeFilterFunctionId: string | null;
  /** Riduzione applicata in € (0 = cap non scattato). */
  appliedReduction: number;
  /**
   * Task CAP evoluto — Metrica del tetto (assente nei snapshot legacy =
   * `amount_eur`).
   */
  capMetric?: CommissioningCapMetric;
  /** Variabile L1 base della percentuale (solo percentage_of_total con base). */
  baseVariableCode?: string | null;
  /**
   * Base % accumulata: storico della variabile-base (incluso questo evento).
   * Assente per CAP assoluto o percentuale legacy auto-referenziale.
   */
  baseTotal?: number | null;
  /** Limite del tetto in unità della metrica (già risolto da % se percentuale). */
  limit?: number;
  /** Cumulato della metrica PRIMA di questo evento. */
  priorMetricTotal?: number;
  /** Valore della metrica portato da questo evento (pre-clamp). */
  eventMetricValue?: number;
  /**
   * Valore della metrica effettivamente contato dopo il clamp (solo metriche
   * valenza/count: quanto dell'evento satura il tetto).
   */
  countedMetricValue?: number;
}

/** Scalare risolvibile a runtime da una variabile L1. */
export type CommissioningRuntimeScalar = string | number | boolean | null;

/** Stati ciclo vita evento (CHECK su commissioning_events.status). */
export const COMMISSIONING_EVENT_STATUSES = [
  'pending',
  'processed',
  'no_match',
  'failed',
  'reversed',
] as const;

/** Stati riga ledger (CHECK su commissioning_event_lines.status). */
export const COMMISSIONING_EVENT_LINE_STATUSES = [
  'wip',
  'confirmed',
  'reversed',
  'recredited',
  // Task #3454 — runtime v3: riga esclusa (base mancante, driver non
  // assegnato, membro fuori cluster, ...). Motivo in snapshot.exclusionReason.
  'excluded',
] as const;

/**
 * Snapshot COMPLETO del calcolo di una riga ledger: il report legge QUI,
 * mai ricalcola ("il ricalcolo scrive, il report legge").
 */
export interface CommissioningLedgerSnapshot extends CommissioningLedgerSnapshotV3Extension {
  driverId: string;
  /** Versioni delle function L3 usate nel calcolo (functionId → version). */

  functionVersions: Record<string, number>;
  /** Variabili L1 risolte al momento del calcolo (code → valore). */

  resolvedVariables: Record<string, CommissioningRuntimeScalar>;
  /** TGT: base membro cluster, override per-store, valore applicato. */

  tgt: { base: number | null; override: number | null; applied: number | null } | null;
  /** Canone congelato (ereditato dal listino o override della base prodotto). */

  canone: { inherited: boolean; value: number | null; multiplier: number | null } | null;
  /**
   * Versione del value package effettivamente usata per la valorizzazione
   * (risolta per data evento nella catena di versioni). Assente/null =
   * istanza senza package o riga pre-versioning.
   */

  valuePackage?: { id: string; version: number } | null;
  /**
   * Base prodotto fotografata usata per la riga
   * (null = package assegnato ma senza base per il prodotto dell'evento).
   */

  multipliers: Record<string, number>;
  /** KPI/metriche di periodo rilevanti al momento del calcolo. */

  kpi: Record<string, number>;
  /** Valori addon.* rilevati in cassa (flatten; multiselect = array canonici). */

  addon: CommissioningAddonRuntimeNamespace;
  /** Effetti DAG applicati alla riga. */

  dagEffects: CommissioningLedgerDagEffect[];
  /** CAP applicato (null = istanza senza CAP). */

  cap: CommissioningLedgerCapEffect | null;
  /**
   * Buste kernel v2 (Task #3453): stato di risoluzione a 5 stati + provenienza
   * per ogni variabile. Opzionale: righe pre-kernel non lo possiedono.
   */

  functionOutcomes?: Record<string, import('./commissioning-kernel').CommissioningFunctionOutcome>;
  /**
   * Tipo 'gettone': esito della selezione per regole del template (quale
   * regola ha selezionato l'evento, o perché il gettone gara è 0). Assente =
   * tipo diverso o riga precedente alla semantica di selezione.
   */
  gettoneSelection?: CommissioningGettoneSelectionTrace;
  /** Pre-threshold facts, not another economic line or payout. */
  thresholdFact?: import('../schemas/commissioning-threshold-measures').CommissioningThresholdFact;
  /** Frozen observations of this evaluation, separate from the reusable recipe. */
  thresholdContext?: import('../schemas/commissioning-threshold-state').CommissioningThresholdEvaluationContext;
  /**
   * Task #3567 — Puntatori alle versioni delle liste di riferimento usate
   * nel calcolo: listRefId → id snapshot content-addressed
   * (commissioning_reference_list_snapshots). Il ricalcolo/replay risolve i
   * puntatori e rivaluta con i valori dell'epoca; le copie della riga
   * (clawback/recredit/carry-forward/relock) li preservano via spread.
   * Assente = riga pre-cutover: ricalcolo con liste live (documentato).
   */
  listSnapshots?: Record<string, string>;
  /** Motivo dello stato wip (finestra event-level in attesa di conferma). */

  wipReason: string | null;

  vpProductBase?: {
    productId: string;
    valenza: number;
    gettone_contrattuale: number;
    gettone_gara: number;
    canone_multiplier: number | null;
    // Stage F: canone_inherited/canone_override rimossi dalla migrazione.
    // Snapshot storici possono ancora contenere questi campi, ignorali.
  } | null;
  /** Motivo esplicito di evento non valorizzabile dal package (fail-closed). */

  vpNotValorizableReason?: string | null;
  /**
   * Prodotto dei canone_factor degli abbinamenti in match/no_match
   * (1 = nessun abbinamento applica). Registrato in snapshot per
   * spiegabilità del contributo canone.
   */
  vpCanoneFactorProduct?: number;
  /**
   * Versioni delle funzioni usate tramite abbinamento istanza
   * (functionId → version). Assente negli snapshot senza abbinamenti.
   */
  instanceFunctionVersions?: Record<string, number>;
  /**
   * Soglie DICHIARATIVE (tipo 'soglie' con thresholdsDeclaration): esito del
   * calcolo per banda applicato alla riga. Assente per righe non-soglie o
   * calcolate dalla sezione aggregate_rules.thresholds (effects generici).
   */
  thresholdDeclared?: {
    source?: import('../utils/commissioning-declarative-thresholds').CommissioningThresholdOrigin;
    mode: 'progressive' | 'regressive';
    countKind: CommissioningThresholdCountKind;
    /** Unità di count dell'evento (valenza | 1 | valore variabile L1). */
    eventUnits: number | null;
    /** Cumulato usato per la selezione banda (incluso questo evento). */
    cumulativeUnits: number | null;
    measurement?: import('../schemas/commissioning-threshold-measures').CommissioningThresholdMeasurement;
    economicUnits?: number | null;
    /** Indice banda 1-based (0 = sotto la prima banda). */
    bandIndex: number;
    /** Nome scaglione applicato (null = nessuna banda). */
    bandName: string | null;
    /** Premi flat one-shot erogati DA QUESTA riga (primo raggiungimento). */
    flatAwarded: Array<{ from: number; name: string | null; amount: number }>;
    /** Scala applicata ai limiti di banda (assente = assoluta, fattore 1). */
    scale?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdScaleTrace;
    economics?: CommissioningThresholdEconomicsTrace;
    admission?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdAdmissionDecision;
  };
  thresholdValidity?: CommissioningThresholdValidity;
  /**
   * Trace per-abbinamento istanza×funzione (leve match/else applicate).
   * Assente negli snapshot senza abbinamenti.
   */
  instanceFunctionTraces?: import('./commissioning-kernel').CommissioningInstanceFunctionTrace[];
  /** Moltiplicatori applicati (chiave descrittiva → fattore). */
}

/** RuntimeContext per evento CRM. */
export interface CommissioningRuntimeContextCrm extends CommissioningRuntimeContextBase {
  kind: 'crm';
  crm: CommissioningRuntimeNamespace;
}

/** Namespaces comuni a tutti i tipi evento. */
export interface CommissioningRuntimeContextBase {
  /** Metadati evento (event.occurred_at, event.driver_id, ...). */
  event: CommissioningRuntimeNamespace;
  /** PDV dove l'evento è stato scaricato. */
  store: CommissioningRuntimeNamespace;
  /** RS derivata dal PDV. */
  company: CommissioningRuntimeNamespace;
  /** Variabili computed aggregate calcolate sul periodo (period_aggregate.*). */
  period_aggregate: CommissioningRuntimeNamespace;
  /** Stable band identity; empty string is a ready, genuinely unreached band. */
  threshold_state?: CommissioningRuntimeNamespace;
}

export type CommissioningCrmTrigger = typeof COMMISSIONING_CRM_TRIGGERS[number];

/** Job: storna un evento (clawback delle righe dell'evento originale). */
export interface CommissioningReverseEventJobData {
  type: 'reverse-event';
  tenantId: string;
  /** Evento ORIGINALE da stornare. */
  originalEventId: string;
  /** Evento negativo (reversal) già persistito che ha innescato lo storno. */
  reversalEventId: string;
}

/**
 * Add-on rilevato in cassa portato dall'evento sell-out (Task #3450, v2):
 * chiavi e valori CANONICI (parameter code → codice/i valore, numero, bool,
 * testo); i multiselect sono array tipizzati. Solo risposte answered.
 */
export interface CommissioningEventAddonSnapshot {
  addonId: string;
  addonName: string;
  versionId: string;
  version: number;
  /** parameter_code → valore canonico (string[] per i multiselect). */
  values: Record<string, string | number | boolean | string[]>;
}

/** Riga commissioning_event_line_revisions (append-only). */
export interface CommissioningEventLineRevision {
  id: string;
  tenant_id: string;
  line_id: string;
  changed_by: string | null;
  reason: string;
  delta: CommissioningEventLineRevisionDelta;
  created_at: string;
}

export type CommissioningEventLineStatus = typeof COMMISSIONING_EVENT_LINE_STATUSES[number];

/** Job: ricalcolo TOTALE di una gara (es. cambio membership cluster). */
export interface CommissioningRecalculateCompetitionJobData {
  type: 'recalculate-competition';
  tenantId: string;
  competitionId: string;
  reason: string;
  requestedBy: string | null;
  /**
   * Politica di ricalcolo: 'from_effective_date' ricalcola solo gli eventi
   * a partire dalla decorrenza (eventi precedenti intatti);
   * 'from_competition_start' ricalcola tutti gli eventi della gara.
   * Se assente, comportamento storico = from_competition_start.
   */
  policy?: 'from_effective_date' | 'from_competition_start';
  /**
   * Data efficacia YYYY-MM-DD per la policy 'from_effective_date'.
   * Ignorato se policy è assente o 'from_competition_start'.
   */
  effectiveDate?: string;
  /**
   * Ricalcolo MIRATO: ricomputa solo gli eventi di questo driver (cambio
   * membership di un cluster gara per driver). Assente = tutti i driver
   * (cluster overall o ricalcolo totale).
   */
  businessDriverId?: string;
  /**
   * Intento durevole `competition_total` (commissioning_recalc_runs) di cui
   * questo job è la consegna: il worker lo reclama (pending→running) prima di
   * ricalcolare e lo chiude con l'esito. Assente = ricalcolo storico senza registro.
   */
  intentRunId?: string;
}

/** Gli 8 trigger CRM che emettono eventi (SOLO se la campagna ha il driver). */
export const COMMISSIONING_CRM_TRIGGERS = [
  'lead_created',
  'lead_won',
  'stage_changed',
  'customer_created',
  'lead_converted',
  'campaign_mass_action',
  'form_submitted',
  'appointment_completed',
] as const;

/** Riga commissioning_event_lines (shape DB → FE). */
/**
 * Forma carry-forward di una riga ledger di competenza lockata (append-only):
 * 'delta' referenzia una riga origine, 'late_accrual' è un maturato tardivo
 * senza origine (ancora immutabile dei futuri delta).
 */
export type CommissioningCarryForwardKind = 'delta' | 'late_accrual';

export interface CommissioningEventLine {
  id: string;
  tenant_id: string;
  event_id: string;
  competition_id: string;
  configurator_instance_id: string;
  snapshot: CommissioningLedgerSnapshot;
  valenza: number;
  gettone_contrattuale: number;
  gettone_gara: number;
  amount_eur: number;
  status: CommissioningEventLineStatus;
  clawback_of_line_id: string | null;
  eligibility_key: string | null;
  /** Periodo di competenza attribuito (Task #3413, null = gara senza periodi). */
  period_id: string | null;
  /** Riga delta carry-forward: origine referenziata (mai UPDATE dell'origine). */
  carry_forward_of_line_id: string | null;
  /** Etichetta leggibile del carry-forward (obbligatoria se referenziante). */
  carry_forward_reason: string | null;
  /**
   * Forma carry-forward (competenza lockata, append-only):
   * 'delta' = rettifica referenziante una riga origine in periodo lockato;
   * 'late_accrual' = maturato tardivo senza origine (la riga stessa è
   * l'ancora immutabile dei futuri delta); null = riga normale.
   */
  carry_forward_kind: CommissioningCarryForwardKind | null;
  created_at: string;
  updated_at: string;
  /** Absent in historical projections; distinct from classification membership. */
  economic_subject_id?: string | null;
  contributor_participant_id?: string | null;
  context_revision?: number | null;
}

/** RuntimeContext per evento sell-out (vendita POS). */
export interface CommissioningRuntimeContextSellOut extends CommissioningRuntimeContextBase {
  kind: 'sell_out';
  sale: CommissioningRuntimeNamespace;
  /** Il set `channel_ids` (canali del listino) resta array: cardinalità set nel kernel. */
  priceListItem: CommissioningAddonRuntimeNamespace;
  product: CommissioningRuntimeNamespace;
  /** Task #4332 — i set (componenti, nature) restano array: il kernel li legge come cardinalità set. */
  composition: CommissioningAddonRuntimeNamespace;
  /** Chiavi flatten `<parameterCode>` e `<addonName>.<parameterCode>`; i multiselect sono array. */
  addon: CommissioningAddonRuntimeNamespace;
}

/** Union discriminata dei job della coda commissioning-engine. */
export type CommissioningEngineJobData =
  | CommissioningProcessEventJobData
  | CommissioningReverseEventJobData
  | CommissioningRecalculateCompetitionJobData
  | import('../schemas/commissioning-threshold-state').CommissioningThresholdRunJob
  | import('../schemas/commissioning-threshold-invalidation').CommissioningThresholdInvalidationJob;

/** Stati periodo di competenza (CHECK su commissioning_competition_periods.status). */
export const COMMISSIONING_PERIOD_STATUSES = ['open', 'locked'] as const;

/**
 * Regola del tipo «Bonus/Malus su KPI»: SOLO struttura. Banda morta e importi
 * vivono nei valori per livello (`rules.<id>.deadBandLow|deadBandHigh|malusValue|bonusValue`).
 */
export interface CommissioningBonusMalusRule {
  /** Identità stabile: chiave dei valori per livello e dei percorsi di completezza. */
  id: string;
  label?: string;
  /** Variabile L1 del KPI osservato (code). */
  kpiVariableCode: string;
  /** Finestra di osservazione; assente = periodo di gara. */
  window?: CommissioningBonusMalusWindow;
  /** Condizione L3 addizionale che abilita la regola. */
  functionId?: string;
  /** Effetti dichiarati (≥1, senza duplicati): decidono QUALI importi l'editor valori richiede. */
  effects: CommissioningBonusMalusEffect[];
}

/** Verso della classifica: 'desc' = vince il valore più alto. */
export const COMMISSIONING_RANKING_DIRECTIONS = ['desc', 'asc'] as const;
export type CommissioningRankingDirection = typeof COMMISSIONING_RANKING_DIRECTIONS[number];

/**
 * Criteri di classifica (tipo 'classifica'): SOLO struttura. La metrica è
 * una delle misure aggregate del periodo oppure (`metric: 'variable'`) una
 * variabile computed di periodo del catalogo (`metricVariableCode`).
 */
export interface CommissioningRankingConfig {
  metric: CommissioningRankingMetric;
  /** Variabile computed di periodo; obbligatoria se `metric === 'variable'`. */
  metricVariableCode?: string;
  direction: CommissioningRankingDirection;
  tieBreaker: import('./commissioning-runtime-v3').CommissioningRankingTieBreaker;
}

/** Metrica della classifica: misura aggregata del periodo o variabile computed. */
export const COMMISSIONING_RANKING_METRICS = ['valenza_sum', 'line_count', 'amount_sum', 'tgt_pct', 'variable'] as const;
export type CommissioningRankingMetric = typeof COMMISSIONING_RANKING_METRICS[number];

/** Sorgente del meta-configuratore 'aggregazione': SOLO struttura (il peso è un valore per livello). */
export interface CommissioningAggregationSource {
  id: string;
  templateId: string;
  label?: string;
}

/** Livello di punteggio dell'Aggregazione: SOLO struttura (punteggio minimo e payout sono valori per livello). */
export interface CommissioningAggregationLevel {
  id: string;
  level: string;
}

/** Task #3410 — Default CAP del template (l'istanza può fare override). */
export interface CommissioningTemplateCapDefaults {
  capType?: CommissioningCapType;
  capValue?: number;
  /** Function L3 che delimita il perimetro su cui si applica il CAP. */
  capScopeFilterFunctionId?: string;
  /**
   * Task CAP evoluto — Metrica su cui insiste il tetto (assente = `amount_eur`,
   * comportamento storico). `valenza`/`count` ammesse solo per il tipo soglie.
   */
  capMetric?: CommissioningCapMetric;
  /**
   * Task CAP evoluto — Variabile L1 (code) base della percentuale: il tetto
   * diventa capValue% dell'accumulato per-evento di questa variabile sullo
   * storico del configuratore (es. "5% dello scaricato in cassa").
   * Obbligatoria per capType `percentage_of_total` sui nuovi salvataggi; i
   * config legacy senza base mantengono a runtime la semantica
   * auto-referenziale (% del maturato dello stesso configuratore).
   */
  capBaseVariableCode?: string;
}

/** Fascia di posizioni premiata in classifica: SOLO struttura (il premio è un valore per livello). */
export interface CommissioningRankingPrize {
  id: string;
  label?: string;
  fromPosition: number;
  /** Assente = solo fromPosition. */
  toPosition?: number;
}

export type CommissioningPeriodLockAction = typeof COMMISSIONING_PERIOD_LOCK_ACTIONS[number];

/** Stati attesa amministrativa gare ricevute (pattern pos_admin_expectations). */
export const COMMISSIONING_RECEIVABLE_EXPECTATION_STATUSES = [
  'expected',
  'documented',
  'reconciled',
] as const;

/** Payout interno per beneficiario utente (accrued IMMUTABILE post-creazione). */
export interface CommissioningPayout {
  id: string;
  tenant_id: string;
  competition_id: string;
  period_id: string;
  user_id: string;
  accrued_amount: number;
  status: CommissioningPayoutStatus;
  approved_at: string | null;
  approved_by: string | null;
  settled_at: string | null;
  settled_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type CommissioningPayoutStatus = typeof COMMISSIONING_PAYOUT_STATUSES[number];

/** Azioni auditate sul periodo (append-only). */
export const COMMISSIONING_PERIOD_LOCK_ACTIONS = ['lock', 'unlock'] as const;

/** Periodo + totali maturati dal ledger (liste UI). */
export interface CommissioningCompetitionPeriodWithTotals extends CommissioningCompetitionPeriod {
  /** Σ amount_eur righe vive (confirmed/recredited) attribuite al periodo. */
  matured_amount: number;
  /** Righe ledger attribuite al periodo (tutti gli stati). */
  line_count: number;
  /** Σ amount_eur delle sole righe delta carry-forward atterrate nel periodo. */
  carry_forward_amount: number;
}

/** Aggiustamento etichettato (append-only): totale = accrued + Σ amount. */
export interface CommissioningPayoutAdjustment {
  id: string;
  tenant_id: string;
  payout_id: string;
  amount: number;
  label: string;
  created_by: string | null;
  created_at: string;
}

export type CommissioningPeriodStatus = typeof COMMISSIONING_PERIOD_STATUSES[number];

/** Payout + beneficiario risolto, aggiustamenti e totale (liste UI). */
export interface CommissioningPayoutListItem extends CommissioningPayout {
  user_name: string | null;
  period_label: string;
  adjustments: CommissioningPayoutAdjustment[];
  adjustments_total: number;
  /** accrued_amount + adjustments_total. */
  total_amount: number;
}

/** Riga audit lock/unlock (chi/quando/perché). */
export interface CommissioningPeriodLockAuditEntry {
  id: string;
  tenant_id: string;
  period_id: string;
  competition_id: string;
  action: CommissioningPeriodLockAction;
  performed_by: string | null;
  /** Nome utente risolto al read (null = utente rimosso o azione di sistema). */
  performed_by_name: string | null;
  notes: string | null;
  created_at: string;
}

export type CommissioningReceivableExpectationStatus =
  typeof COMMISSIONING_RECEIVABLE_EXPECTATION_STATUSES[number];

/** Stati liquidazione payout gare risorse (avanzamento monotono). */
export const COMMISSIONING_PAYOUT_STATUSES = ['maturato', 'approvato', 'liquidato'] as const;

/** Attesa amministrativa di una gara ricevuta (insegna/sellin) per periodo. */
export interface CommissioningReceivableExpectation {
  id: string;
  tenant_id: string;
  competition_id: string;
  period_id: string;
  expected_amount: number;
  status: CommissioningReceivableExpectationStatus;
  document_ref: string | null;
  documented_amount: number | null;
  documented_at: string | null;
  documented_by: string | null;
  reconciled_at: string | null;
  reconciled_by: string | null;
  notes: string | null;
  /** documented_amount - expected_amount (null finché non documentata). */
  delta_amount: number | null;
  created_at: string;
  updated_at: string;
}

/** Esito del lock periodo (POST /periods/:id/lock). */
export interface CommissioningPeriodLockResult {
  period: CommissioningCompetitionPeriod;
  /** Attesa generata/aggiornata (solo famiglie insegna/sellin). */
  expectation: CommissioningReceivableExpectation | null;
  /** Payout consolidati o aggiustati (solo famiglia risorse). */
  payouts_upserted: number;
}

/** Periodo di competenza di una gara (lock = righe ledger immutabili). */
export interface CommissioningCompetitionPeriod {
  id: string;
  tenant_id: string;
  competition_id: string;
  label: string;
  period_start: string;
  period_end: string;
  status: CommissioningPeriodStatus;
  locked_at: string | null;
  locked_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Attesa + metadati periodo (liste UI). */
export interface CommissioningReceivableExpectationListItem
  extends CommissioningReceivableExpectation {
  period_label: string;
  period_start: string;
  period_end: string;
}

/**
 * Manifesto di un package (versione risolta).
 * Stage F: `facts` rimosso (scenari e fact-codes erano la vecchia logica
 * condizionale; le funzioni VP risolvono le variabili a runtime via engine).
 */
export interface CommissioningVpFactsManifest {
  package_id: string;
  version: number;
  event_family: CommissioningVpEventFamily;
}

export type CommissioningVpEventFamily = typeof COMMISSIONING_VP_EVENT_FAMILIES[number];

export type CommissioningMoltiplicatoreTarget =
  typeof COMMISSIONING_MOLTIPLICATORE_TARGETS[number];

/**
 * Riga ledger arricchita coi campi evento (JOIN su commissioning_events).
 * Shape esatta della risposta BE di GET /incentivazione/ledger/competitions/:id/lines.
 */
export interface LedgerLineWithEvent extends CommissioningEventLine {
  /** Current display name of the contributor identity saved on the line.
   * Not a beneficiary or an individual share of a collective award. */
  contributor_label?: string | null;
  /** Source type dell'evento padre (sell_out | crm | sell_in). */
  event_source_type: CommissioningEventSourceType;
  /** Data/ora di competenza dell'evento padre (text ISO). */
  event_occurred_at: string;
  /** Stato dell'evento padre. */
  event_status: CommissioningEventStatus;
}

/** Paletto v2 function L3 (con discriminante esplicito; retrocompatibile col legacy). */
export interface CommissioningGateFunctionL3 {
  kind: 'function_l3';
  functionId: string;
  /** Layer override opzionale (solo per kind function_l3). */
  layerOverride?: CommissioningInstanceScope;
}

/** Paletto v2 a fatturato aggregato nel perimetro dichiarato. */
export interface CommissioningGateFatturato {
  kind: 'fatturato';
  /** Soglia minima: il paletto è soddisfatto se amount_sum >= threshold. */
  threshold: number;
  perimeter: CommissioningGatePerimeter;
}

/** Perimetro di aggregazione del paletto (indipendente dallo scope del configuratore). */
export interface CommissioningGatePerimeter {
  /** Dimensione di aggregazione (RS, PDV o risorsa). */
  axis: CommissioningGateAxis;
  /**
   * Cardinalità: single = solo l'asse dell'evento corrente (store/company/user);
   * multi = aggregato su tutti gli eventi dell'istanza nel periodo.
   */
  cardinality: CommissioningGateCardinality;
}

/**
 * Paletto v2 entity_count: almeno `minEntities` entità distinte sull'asse
 * `entityAxis` che superano `entityThreshold` sulla metrica indicata.
 * Esempio: "almeno 3 PDV con valenza_sum >= 10".
 */
export interface CommissioningGateEntityCount {
  kind: 'entity_count';
  /** Metrica da valutare per-entità (valenza_sum | line_count | amount_sum). */
  metric: 'valenza_sum' | 'line_count' | 'amount_sum';
  /** Soglia minima per singola entità (es. valenza >= 10). */
  entityThreshold: number;
  /** Numero minimo di entità distinte che devono superare la soglia. */
  minEntities: number;
  /** Asse su cui contare le entità distinte (rs | pdv | user). */
  entityAxis: CommissioningGateAxis;
}

/** Cardinalità del perimetro: single = asse dell'evento corrente; multi = tutti. */
export type CommissioningGateCardinality = 'single' | 'multi';

/** Paletto v2 a valenza aggregata nel perimetro dichiarato. */
export interface CommissioningGateValenza {
  kind: 'valenza';
  /** Soglia minima: il paletto è soddisfatto se valenza_sum >= threshold. */
  threshold: number;
  perimeter: CommissioningGatePerimeter;
}

/** Paletto v2 a count linee aggregato nel perimetro dichiarato. */
export interface CommissioningGateCount {
  kind: 'count';
  /** Soglia minima: il paletto è soddisfatto se line_count >= threshold. */
  threshold: number;
  perimeter: CommissioningGatePerimeter;
}

/** Task #3493 — Paletto v2: union discriminata (`kind` obbligatorio). */
export type CommissioningGateV2 =
  | CommissioningGateFunctionL3
  | CommissioningGateValenza
  | CommissioningGateFatturato
  | CommissioningGateCount
  | CommissioningGateEntityCount;

export type CommissioningEdgeConditionOperator =
  typeof COMMISSIONING_EDGE_CONDITION_OPERATORS[number];

/** Risposta paginata di GET /incentivazione/ledger/competitions/:id/lines. */
export interface LedgerLinesResponse {
  competition_id: string;
  lines: LedgerLineWithEvent[];
  total: number;
  limit: number;
  offset: number;
}

/** Risposta di GET /incentivazione/ledger/lines/:id/revisions. */
export interface LedgerRevisionsResponse {
  line_id: string;
  revisions: LedgerRevisionWithAuthor[];
}

/**
 * Revisione riga con nome autore risolto (LEFT JOIN users nel BE).
 * Estende CommissioningEventLineRevision aggiungendo il nome leggibile.
 */
export interface LedgerRevisionWithAuthor extends CommissioningEventLineRevision {
  /** Nome e cognome dell'utente che ha operato la modifica;
   *  null = motore automatico o utente cancellato. */
  changed_by_name: string | null;
}

/** Operatori di confronto per la condizione `parent_outcome`. */
export const COMMISSIONING_EDGE_CONDITION_OPERATORS = ['gte', 'gt', 'lte', 'lt', 'eq'] as const;

/** Condizione arco basata su funzione L3. */
export interface EdgeConditionL3 {
  kind: 'l3_function';
  functionId: string;
}

export type CommissioningEdgeConditionMetric =
  typeof COMMISSIONING_EDGE_CONDITION_METRICS[number];

/** Union discriminata: una condizione arco può essere L3 o esito del padre. */
export type EdgeCondition = EdgeConditionL3 | EdgeConditionParentOutcome;

/**
 * Condizione arco basata sul risultato del padre già calcolato in ordine
 * topologico (mai ricalcola il padre; l'anti-ciclo garantisce il padre
 * processato prima del figlio).
 */
export interface EdgeConditionParentOutcome {
  kind: 'parent_outcome';
  metric: CommissioningEdgeConditionMetric;
  operator: CommissioningEdgeConditionOperator;
  threshold: number;
}

/** Campo target del moltiplicatore. */
export const COMMISSIONING_MOLTIPLICATORE_TARGETS = [
  'amount_eur',
  'gettone_gara',
  'both',
] as const;

/**
 * Filtro validità listino:
 *   'current' = in vigore oggi (valid_from ≤ oggi AND (valid_to IS NULL OR valid_to ≥ oggi))
 *   'future'  = valid_from > oggi
 * Entrambi selezionati = OR (tutti i listini non scaduti).
 * Nessuno selezionato = default 'current'.
 */
export type CommissioningVpValidityFilter = 'current' | 'future';

/** Risposta di GET /value-packages/products/search. */
export interface CommissioningVpProductSearchResult {
  products: CommissioningVpPickerProductRow[];
  facets: {
    suppliers: CommissioningVpFacetOption[];
    price_lists: CommissioningVpFacetOption[];
    categories: CommissioningVpFacetOption[];
    business_drivers: CommissioningVpFacetOption[];
    list_types: CommissioningVpFacetOption[];
    product_natures: CommissioningVpFacetOption[];
  };
  total: number;
  /** true se il tenant ha almeno un listino canvas attivo. */
  has_any_canvas: boolean;
  /**
   * Numero di listini (nei list_types correnti) con valid_from > oggi.
   * Utile per il badge "Includi listini futuri".
   */
  future_price_lists_count: number;
}

/**
 * Risposta di GET /value-packages/price-lists/search.
 * Task #3547 — con `facets_only=1` la query listini è saltata: `price_lists`
 * è vuoto e `total_matching_products` è 0; sono significativi solo `facets`
 * (self-excluding, stessa semantica validità/filtri) e
 * `future_price_lists_count`.
 */
export interface CommissioningVpPriceListSearchResult {
  price_lists: CommissioningVpPriceListRow[];
  /**
   * Task #3537 — Facets self-excluding per la toolbar filtri del picker
   * listino-first: ogni dimensione è calcolata escludendo il proprio filtro
   * ma mantenendo tutti gli altri (le pill restano visibili anche a zero
   * risultati finché il catalogo ha opzioni). Conteggi = prodotti matchanti.
   */
  facets: {
    suppliers: CommissioningVpFacetOption[];
    categories: CommissioningVpFacetOption[];
    business_drivers: CommissioningVpFacetOption[];
    list_types: CommissioningVpFacetOption[];
    product_natures: CommissioningVpFacetOption[];
  };
  /** Numero totale di prodotti matchanti across tutti i listini correnti. */
  total_matching_products: number;
  /** Numero di listini futuri (valid_from > oggi) nei list_types selezionati. */
  future_price_lists_count: number;
}

/** Voce facet picker con conteggio prodotti matchanti. */
export interface CommissioningVpFacetOption {
  id: string;
  name: string;
  count: number;
}

/**
 * Riga prodotto nel picker, versione condivisa con campo already_in_vp.
 * Ritornata da GET /value-packages/products/search (con o senza vp_id).
 */
export interface CommissioningVpPickerProductRow {
  product_id: string;
  product_name: string;
  sku: string | null;
  product_nature: string;
  price_list_id: string;
  price_list_name: string;
  price_list_type: string;
  price_list_valid_from: string | null;
  price_list_valid_to: string | null;
  price_list_is_default: boolean;
  price_list_priority: number;
  monthly_fee: number | null;
  category_id: string | null;
  category_name: string | null;
  business_driver_id: string | null;
  business_driver_name: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  /**
   * true se il product_id è già presente come base nel VP indicato da vp_id.
   * Sempre false se vp_id non è stato fornito nella richiesta.
   */
  already_in_vp: boolean;
}

/**
 * Riga listino nella vista listino-first del picker.
 * Ritornata da GET /value-packages/price-lists/search.
 */
export interface CommissioningVpPriceListRow {
  price_list_id: string;
  price_list_name: string;
  price_list_type: string;
  price_list_valid_from: string | null;
  price_list_valid_to: string | null;
  price_list_is_default: boolean;
  price_list_priority: number;
  supplier_id: string | null;
  supplier_name: string | null;
  /** Numero di prodotti nel listino che matchano i filtri correnti (q, natures, ecc.). */
  matching_product_count: number;
  /** Numero totale di prodotti attivi nel listino (senza filtro q/natures). */
  total_product_count: number;
}

/** Valori economici opzionali per un prodotto nel batch add. */
export interface CommissioningVpProductValuesInput {
  valenza?: number;
  gettone_contrattuale?: number;
  gettone_gara?: number;
  canone_multiplier?: number | null;
  canone_gara?: number | null;
}

export const COMMISSIONING_CROSS_REF_MEASURES: ReadonlyArray<CommissioningCrossRefMeasure> = [
  'valenza_sum',
  'line_count',
  'amount_sum',
  'ratio',
  'tgt_pct',
  'threshold_reached',
  'effective_band_id',
  'prize_earned',
  'malus_count',
  'bonus_count',
  'bonus_malus_sum',
] as const;

/** Misure cross_ref sui risultati di periodo bonus/malus della sorgente. */
export const COMMISSIONING_CROSS_REF_BONUS_MALUS_MEASURES = [
  'malus_count',
  'bonus_count',
  'bonus_malus_sum',
] as const satisfies ReadonlyArray<CommissioningCrossRefMeasure>;

/** Misure cross_ref BOOLEANE (1/0): ammettono le combinazioni all/any/last. */
export const COMMISSIONING_CROSS_REF_BOOLEAN_MEASURES = [
  'threshold_reached',
] as const satisfies ReadonlyArray<CommissioningCrossRefMeasure>;

export function isCommissioningCrossRefBooleanMeasure(
  m: CommissioningCrossRefMeasure,
): m is (typeof COMMISSIONING_CROSS_REF_BOOLEAN_MEASURES)[number] {
  return (COMMISSIONING_CROSS_REF_BOOLEAN_MEASURES as ReadonlyArray<string>).includes(m);
}

export function isCommissioningCrossRefBonusMalusMeasure(
  m: CommissioningCrossRefMeasure,
): m is (typeof COMMISSIONING_CROSS_REF_BONUS_MALUS_MEASURES)[number] {
  return (COMMISSIONING_CROSS_REF_BONUS_MALUS_MEASURES as ReadonlyArray<string>).includes(m);
}

export const COMMISSIONING_CROSS_REF_WINDOW_KINDS: ReadonlyArray<CommissioningCrossRefWindowKind> = [
  'overlap',
  'previous_periods',
  'source_to_date',
  'source_full',
] as const;

export const COMMISSIONING_CROSS_REF_COMBINES: ReadonlyArray<CommissioningCrossRefCombine> = [
  'sum',
  'avg',
  'min',
  'all',
  'any',
  'last',
] as const;

/** Combinazioni ammesse per le misure booleane (threshold_reached). */
export const COMMISSIONING_CROSS_REF_BOOLEAN_COMBINES = [
  'all',
  'any',
  'last',
] as const satisfies ReadonlyArray<CommissioningCrossRefCombine>;

/** Combinazioni ammesse per le misure numeriche. */
export const COMMISSIONING_CROSS_REF_NUMERIC_COMBINES = [
  'sum',
  'avg',
  'min',
  'last',
] as const satisfies ReadonlyArray<CommissioningCrossRefCombine>;

/** Combinazioni ammesse per una misura cross_ref (unica fonte FE/BE/Zod). */
export function commissioningCrossRefCombinesForMeasure(
  measure: CommissioningCrossRefMeasure,
): ReadonlyArray<CommissioningCrossRefCombine> {
  if (measure === 'effective_band_id') return ['last'];
  return isCommissioningCrossRefBooleanMeasure(measure)
    ? COMMISSIONING_CROSS_REF_BOOLEAN_COMBINES
    : COMMISSIONING_CROSS_REF_NUMERIC_COMBINES;
}

/**
 * Finestra effettiva di una variabile cross_ref: `refWindow` oppure il
 * default storico (sovrapposizione col periodo corrente).
 */
export function resolveCommissioningCrossRefWindow(
  cfg: Pick<CommissioningVariableComputeConfig, 'refWindow'> | null | undefined,
): CommissioningCrossRefWindow {
  return cfg?.refWindow ?? { kind: 'overlap' };
}

/**
 * Combinazione effettiva di una variabile cross_ref: `refCombine` oppure il
 * default che riproduce il comportamento storico delle singole misure
 * (threshold_reached = "vero in almeno un periodo", tgt_pct = "ultimo
 * periodo", tutte le altre = somma dei periodi).
 */
export function resolveCommissioningCrossRefCombine(
  cfg: Pick<CommissioningVariableComputeConfig, 'refCombine' | 'refMeasure'> | null | undefined,
  measure: CommissioningCrossRefMeasure,
): CommissioningCrossRefCombine {
  if (cfg?.refCombine) return cfg.refCombine;
  if (measure === 'threshold_reached') return 'any';
  if (measure === 'tgt_pct' || measure === 'effective_band_id') return 'last';
  return 'sum';
}

/**
 * Misure cross_ref lette live dal ledger dell'istanza referenziata
 * (ammettono il filtro L3 opzionale refFilterFunctionId).
 */
export const COMMISSIONING_CROSS_REF_LEDGER_MEASURES = [
  'valenza_sum',
  'line_count',
  'amount_sum',
] as const satisfies ReadonlyArray<CommissioningCrossRefMeasure>;

/**
 * Misure cross_ref ammesse quando il bersaglio è un intero DRIVER DI GARA
 * (refDriver): misure ledger sommate sulle istanze del driver, ratio, TGT %
 * raggiunto (Σ valenza / Σ TGT) e misure bonus/malus (conteggi/somma su tutte
 * le istanze del driver). threshold_reached/prize_earned restano per-istanza
 * (una soglia/premio appartiene a un configuratore, non a un driver).
 */
export const COMMISSIONING_CROSS_REF_DRIVER_MEASURES = [
  'valenza_sum',
  'line_count',
  'amount_sum',
  'ratio',
  'tgt_pct',
  'malus_count',
  'bonus_count',
  'bonus_malus_sum',
] as const satisfies ReadonlyArray<CommissioningCrossRefMeasure>;

/**
 * Risolve il bersaglio di una variabile cross_ref (istanza singola o driver di
 * gara). Null se la config non è cross_ref o non ha esattamente un bersaglio
 * (fail-closed: il chiamante tratta null come non risolvibile).
 */
export function resolveCommissioningCrossRefTarget(
  cfg: Pick<CommissioningVariableComputeConfig, 'aggregation' | 'refInstanceId' | 'refDriver'> | null | undefined,
): CommissioningCrossRefTarget | null {
  if (!cfg || cfg.aggregation !== 'cross_ref') return null;
  const hasInstance = typeof cfg.refInstanceId === 'string' && cfg.refInstanceId.length > 0;
  const hasDriver = cfg.refDriver !== undefined;
  if (hasInstance === hasDriver) return null;
  if (hasInstance && cfg.refInstanceId) return { kind: 'instance', instanceId: cfg.refInstanceId };
  if (cfg.refDriver) {
    return {
      kind: 'driver',
      competitionId: cfg.refDriver.competitionId,
      businessDriverId: cfg.refDriver.businessDriverId,
    };
  }
  return null;
}
export type CommissioningThresholdCountKind =
  typeof COMMISSIONING_THRESHOLD_COUNT_KINDS[number];

/** Tipo di count su cui lavorano le soglie dichiarative. */
export const COMMISSIONING_THRESHOLD_COUNT_KINDS = [
  /** Valenza dal VP (post leve istanza). */
  'valenza',
  /** Conteggio eventi (1 per riga contata). */
  'eventi',
  /** Variabile L1 numerica del catalogo (es. volumi/pezzi). */
  'variabile',
] as const;

export type CommissioningThresholdSource = typeof COMMISSIONING_THRESHOLD_SOURCES[number];

/**
 * Dichiarazione a priori del configuratore a soglie: gli scaglioni ereditano
 * (compilano SOLO i campi delle fonti dichiarate).
 * - progressive: ogni evento resta pagato alla banda in cui è caduto;
 * - regressive: al passaggio di banda lo storico viene rivalutato alla banda
 *   raggiunta (storno append-only + riaccredito via ricalcolo).
 */
export interface CommissioningThresholdDeclaration {
  mode: 'progressive' | 'regressive';
  countKind: CommissioningThresholdCountKind;
  /** Obbligatorio quando countKind = 'variabile' (code L1 del catalogo). */
  countVariableCode?: string;
  /** Omitted keeps the historical additive event semantics. */
  countMode?: 'event_contribution' | 'period_measure';
  /** Required for €/unit paid against a non-additive period indicator. */
  paymentUnit?: import('../schemas/commissioning-threshold-measures').CommissioningThresholdPaymentUnit;
  /** Fonti economiche abilitate (≥ 1, senza duplicati). */
  sources: CommissioningThresholdSource[];
  /** Scala dei limiti di banda; assente = `absolute` (nessun cambiamento per i template esistenti). */
  thresholdScale?: CommissioningThresholdScale;
}

/** Esito tri-state di una funzione L3 valutata dal simulatore. */
export type CommissioningSimFunctionOutcome = 'match' | 'no_match' | 'not_evaluable';

/** Stadio CAP: tetto sulla metrica dichiarata (importo/valenza/conteggio). */
export interface CommissioningTemplateSimStageCap {
  /** True = CAP configurato sul template. */
  configured: boolean;
  /** True = l'evento è nel perimetro CAP (scope filter, fail-closed). */
  subject: boolean;
  scopeOutcome: CommissioningSimFunctionOutcome | null;
  capMetric: CommissioningCapMetric | null;
  /** Limite del tetto in unità della metrica (già risolto da % se percentuale). */
  limit: number | null;
  /** Base % accumulata (storico variabile-base + evento); null se non percentuale. */
  baseTotal: number | null;
  priorMetricTotal: number | null;
  eventMetricValue: number | null;
  countedMetricValue: number | null;
  /** Importo € della riga prima/dopo il tetto. */
  amountBefore: number;
  amountAfter: number;
  reduction: number;
  explanation: string;
}

/** Funzione referenziata dal config valutata sull'evento simulato. */
export interface CommissioningTemplateSimFunctionRow {
  functionId: string;
  name: string | null;
  /** Ruolo della funzione nella configurazione. */
  role: 'conta' | 'remunera' | 'cap_scope' | 'bonus_malus' | 'gate' | 'leva';
  outcome: CommissioningSimFunctionOutcome;
}

/** Stadio BANDA: in che scaglione cade l'evento e con quale cumulato. */
export interface CommissioningTemplateSimStageBanda {
  admission?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdAdmissionDecision;
  bandIndex: number;
  bandName: string | null;
  cumulativeUnits: number | null;
  mode: 'progressive' | 'regressive';
  explanation: string;
  economics?: CommissioningThresholdEconomicsTrace;
  /** Scala delle soglie applicata (solo `per_store`): limiti dichiarati vs effettivi della banda raggiunta. */
  scale?: import('../schemas/commissioning-threshold-economics').CommissioningThresholdScaleTrace;
}

/** Stadio BONUS/MALUS sul totale post-CAP. */
export interface CommissioningTemplateSimStageBonusMalus {
  /** Totale € del periodo post-CAP (maturato precedente + evento). */
  baseAmount: number;
  bonusTotal: number;
  malusTotal: number;
  finalAmount: number;
  rows: Array<{
    code: string;
    list: 'bonus' | 'malus';
    label: string | null;
    /** true/false = funzione valutata; null = non valutabile (fail-closed). */
    applied: boolean | null;
    delta: number;
    explanation: string;
  }>;
  explanation: string;
}

/** Stadio LEVE: abbinamenti istanza×funzione (delta su valenza/gettoni/canone). */
export interface CommissioningTemplateSimStageLeve {
  /** True = valutate le leve reali di un'istanza (instance_id fornito). */
  evaluated: boolean;
  deltaValenza: number;
  deltaGettoneContrattuale: number;
  deltaGettoneGara: number;
  canoneFactorProduct: number;
  /** Esito per singola leva (solo con instance_id). */
  rows: Array<{
    functionId: string;
    functionName: string | null;
    outcome: CommissioningSimFunctionOutcome;
    appliedSide: 'match' | 'else' | 'none';
  }>;
  explanation: string;
}

export type CommissioningCapMetric = typeof COMMISSIONING_CAP_METRICS[number];

/**
 * Risultato della simulazione di configurazione: percorso completo dell'evento
 * tipo attraverso i 6 stadi del modello (conta → leve → banda → remunera →
 * CAP → bonus/malus) con gli importi intermedi. Stadi null = non configurati.
 */
export interface CommissioningTemplateSimulationResult {
  periodComparison?: import('../schemas/commissioning-threshold-simulation').CommissioningThresholdPeriodComparison;
  /** Missing data is distinct from a verified zero payout. */
  validity?: CommissioningThresholdValidity;
  stages: {
    conta: CommissioningTemplateSimStageConta;
    leve: CommissioningTemplateSimStageLeve;
    banda: CommissioningTemplateSimStageBanda | null;
    remunera: CommissioningTemplateSimStageRemunera | null;
    cap: CommissioningTemplateSimStageCap | null;
    bonusMalus: CommissioningTemplateSimStageBonusMalus | null;
  };
  /** Importo € della riga evento dopo il CAP. */
  eventAmountEur: number;
  /** Totale periodo dopo bonus/malus (= baseAmount se pattern assente). */
  finalAmountEur: number;
  /** Valenza contata della riga dopo l'eventuale clamp CAP su valenza. */
  eventValenza: number;
  warnings: string[];
}

/** Stadio CONTA: l'evento passa il filtro di conteggio? Con quante unità? */
export interface CommissioningTemplateSimStageConta {
  /** True = l'evento è contato dal configuratore. */
  passed: boolean;
  filterFunctionId: string | null;
  filterOutcome: CommissioningSimFunctionOutcome | null;
  /** Unità di count dell'evento (valenza | 1 | variabile L1); null = non risolvibili. */
  eventUnits: number | null;
  countKind: CommissioningThresholdCountKind | null;
  explanation: string;
}

/** Stadio REMUNERA: composizione dell'importo per fonte dichiarata. */
export interface CommissioningTemplateSimStageRemunera {
  /** True = l'evento è remunerato (filtro REMUNERA passato). */
  remunerated: boolean;
  filterFunctionId: string | null;
  filterOutcome: CommissioningSimFunctionOutcome | null;
  canoneContribution: number;
  gettoneGara: number;
  perUnitAmount: number;
  /** Fonte '% sull'importo': valore banda / 100 × importo netto dell'evento. */
  percentOfAmountAmount: number;
  flatAwarded: Array<{ from: number; name: string | null; amount: number }>;
  /** Importo € della riga prima del CAP. */
  amountEur: number;
  explanation: string;
  /** Tipo 'gettone': esito per regola della selezione (assente per Soglie). */
  gettoneSelection?: CommissioningGettoneSelectionTrace;
}

/** Type guard: misura cross_ref calcolata sul ledger (filtrabile per-riga). */
export function isCommissioningCrossRefLedgerMeasure(
  m: CommissioningCrossRefMeasure,
): m is (typeof COMMISSIONING_CROSS_REF_LEDGER_MEASURES)[number] {
  return (COMMISSIONING_CROSS_REF_LEDGER_MEASURES as ReadonlyArray<string>).includes(m);
}
